import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Search,
  X,
  ChevronLeft,
  Sparkles,
  User,
  Home as HomeIcon,
  BellRing,
  Timer,
  FileText,
  ShoppingCart,
  CheckCircle2,
  ChevronRight,
  Receipt,
  Star,
  MessageSquare,
  Clock,
  CreditCard,
  AlertCircle,
  ChefHat,
  RefreshCw,
  Package,
  ChevronDown,
  ChevronUp,
  Hourglass,
  Phone,
  Award,
  History,
  LogOut,
  Wallet,
  Wifi,
  WifiOff,
  Banknote,
  Percent,
  Tag,
  Droplets,
  BookOpen,
  Pencil,
  HelpCircle,
  Loader2,
} from 'lucide-react';
import GooglePayButton from '@google-pay/button-react';
// react-toastify QR menüde artık kullanılmıyor; bildirimler DynamicIsland'a gidiyor.
import { img_url } from '../../api/index';
import { useDynamicIsland } from './DynamicIsland';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useCustomerFavorites } from '../../context/CustomerFavoritesContext';
import RatingPanel, { RatingModal, RatedBadge } from './RatingPanel';
import {
  rateRestaurant as apiRateRestaurant,
  rateWaiter as apiRateWaiter,
} from '../../api/customerRatings';
import ProductCard, { QrProduct } from './ProductCard';
import CategoryNav, { QrCategory } from './CategoryNav';
import MenuStories, { QrStory } from './MenuStories';
import Cart from './Cart';
import ProductDetailModal from './ProductDetailModal';
import ProfileModal, { QrUserProfile } from './ProfileModal';
import FeedbackModal, { FeedbackPayload } from './FeedbackModal';
// QRDynamicIslandNotification kaldırıldı — tüm bildirimler global DynamicIsland
// (src/components/qrmenuV2/DynamicIsland.tsx) üzerinden çalışıyor.
import type { QrCartNotification } from './QRDynamicIslandNotification';
import { useQrLanguage } from './LanguageContext';
import {
  QrAdisyonResponse,
  QrAdisyonCustomer,
  QrCustomerLifetime,
  QrHistoryResponse,
} from '../../api/qrAdisyon';
import {
  QrWaiterActiveCall,
  QrWaiterReasonCode,
  QR_WAITER_REASON_CODES,
} from '../../api/qrWaiterCall';

export interface QrCartItem {
  id: string | number;
  detail_id?: string | number | null;
  name: string;
  price: number | string;
  quantity: number;
  image?: string | null;
}

export interface QrApprovedOrder {
  order_id?: string | number;
  total_price?: number;
  stocks?: Array<{ name: string; quantity: number; price: number | string }>;
}

export type QrTrackingStep = 'received' | 'preparing' | 'ready' | 'served' | 'canceled';
export type QrTrackingStatus = 'pending_approval' | 'approved' | 'completed' | 'canceled';

export interface QrTrackingTimelineItem {
  step: QrTrackingStep;
  label: string;
  completed: boolean;
  is_current: boolean;
  occurred_at: string | null;
  estimated_at: string | null;
}

export interface QrTrackingItemsSummary {
  total: number;
  ready: number;
  preparing: number;
  pending: number;
}

export interface QrTrackingItem {
  type: 'stock' | 'stock_set';
  name: string;
  quantity: number;
  kitchen_status: 'pending' | 'preparing' | 'ready';
  is_printed: boolean;
}

export interface QrTrackingOrder {
  order_id: number;
  order_number: string;
  status: QrTrackingStatus;
  is_canceled: boolean;
  current_step: QrTrackingStep;
  current_step_label: string;
  estimated_total_minutes: number;
  estimated_ready_at: string | null;
  estimated_remaining_seconds: number;
  timeline: QrTrackingTimelineItem[];
  items_summary: QrTrackingItemsSummary;
  items: QrTrackingItem[];
}

interface QRMenuPageProps {
  // Restaurant
  restaurantName?: string;
  restaurantLogo?: string;
  restaurantImage?: string;
  masaName?: string;

  // Data
  categories: QrCategory[];
  products: QrProduct[];
  stories: QrStory[];
  cartItems: QrCartItem[];
  approvedOrders: QrApprovedOrder[];
  pendingOrders: QrApprovedOrder[];
  trackingOrders?: QrTrackingOrder[];
  trackingLoading?: boolean;
  trackingLastUpdatedAt?: number;

  // QR Adisyon (live bill) — fed by /api/qr/{token}/adisyon plus realtime hook
  adisyon?: QrAdisyonResponse | null;
  adisyonLoading?: boolean;
  adisyonError?: string | null;
  adisyonLastUpdatedAt?: number;

  // Customer identify (phone) — gates loyalty + history
  qrCustomer?: QrAdisyonCustomer | null;
  qrLifetime?: QrCustomerLifetime | null;
  identifyLoading?: boolean;
  identifyError?: string | null;
  identifyNotFound?: boolean;
  onIdentifyCustomer?: (phone: string) => void | Promise<void>;
  onForgetCustomer?: () => void;

  // Customer history (paginated)
  qrHistory?: QrHistoryResponse | null;
  historyLoading?: boolean;
  historyError?: string | null;
  onLoadHistory?: (page: number, perPage?: number) => void | Promise<void>;

  // QR Waiter call (real API) — replaces the legacy `onCallWaiter` stub.
  // The parent owns the canonical state; this component only renders + emits.
  activeWaiterCall?: QrWaiterActiveCall | null;
  waiterCallLoading?: boolean;
  waiterCallError?: string | null;
  onSubmitWaiterCall?: (
    reasonCode: QrWaiterReasonCode,
    note?: string | null,
  ) => void | Promise<void>;
  onCancelWaiterCall?: () => void | Promise<void>;

  // State / config
  selectedCategory: string | number;
  searchQuery: string;
  /** @deprecated cart bildirimleri artık global DynamicIsland üzerinden gidiyor. */
  cartNotification?: QrCartNotification | null;
  currency?: string;

  // Theme
  theme: 'light' | 'dark';
  onToggleTheme: () => void;

  // User
  user?: QrUserProfile | null;
  onUserChange?: (user: QrUserProfile | null) => void;
  googleClientId?: string;
  appleServiceId?: string;

  // Payment
  paymentEnabled?: boolean;
  paymentAmount?: number;
  googlePayConfig?: any;
  isProcessingPayment?: boolean;
  applePayAvailable?: boolean;

  // Callbacks
  onSearch: (q: string) => void;
  onCategoryChange: (id: string | number) => void;
  onAddToCart: (product: QrProduct, quantity?: number, detailId?: string | number | null) => void;
  onUpdateQuantity: (
    id: string | number,
    quantity: number,
    detailId?: string | number | null,
  ) => void;
  onRemoveItem: (id: string | number, detailId?: string | number | null) => void;
  onCheckout: () => void;
  /** @deprecated Use onSubmitWaiterCall + onCancelWaiterCall. Kept for back-compat. */
  onCallWaiter?: () => void;
  /** @deprecated Global DynamicIsland kendi auto-dismiss'ini kullanıyor. */
  onDismissNotification?: () => void;
  onBackToWelcome: () => void;
  onPayOnline: () => void;
  onPayGooglePay: (paymentData: any) => void;
  onPayApplePay: () => void;
  onFeedback?: (payload: FeedbackPayload) => void;
}

type Tab = 'home' | 'tracking' | 'waiter' | 'history';

const HERO_DEFAULT =
  'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1080&auto=format&fit=crop';

const formatPrice = (price: number | string) => {
  const n = typeof price === 'string' ? parseFloat(price) : price;
  if (isNaN(n)) return '0.00';
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getImg = (img?: string | null) => {
  if (!img) return '';
  return img.startsWith('http') ? img : `${img_url}/${img}`;
};

// Defensive text renderer — the backend occasionally returns nested objects
// (localised shapes like { tr, en, az }, wrapper objects like { stock: {
// name } } / { product: { title } }, or arrays of tags) where the TypeScript
// types promise plain strings. Using such a value directly in JSX prints the
// dreaded "[object Object]". This helper flattens every common shape into a
// printable string so the UI stays readable even when the API contract drifts.
//
// Resolution order for objects:
//   1. Common localised keys (tr → az → en → ru)
//   2. Common wrapper keys (name → label → title → text → value …)
//   3. Recurse into common nested-entity keys (stock → product → item → data …)
//   4. The first primitive property value (last-resort fallback)
// Arrays are mapped recursively and joined with ", ".
const LOCALE_KEYS = ['tr', 'az', 'en', 'ru'];
const TEXT_KEYS = [
  'name',
  'label',
  'title',
  'text',
  'value',
  'display_name',
  'full_name',
  'summary',
  'description',
];
// When none of the direct text keys match, try to walk *through* these
// common wrapper keys (one level deeper) before giving up. Covers payloads
// like { stock: { name: "Çay" } } or { product: { title: "Çay" } }.
const NESTED_KEYS = [
  'stock',
  'stock_set',
  'product',
  'item',
  'data',
  'pivot',
  'details',
  'detail',
  'category',
];

export const safeText = (value: unknown, depth = 0): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value
      .map((v) => safeText(v, depth + 1))
      .map((s) => s.trim())
      .filter(Boolean)
      .join(', ');
  }
  if (typeof value === 'object') {
    // Bail after a reasonable depth so a cyclic/huge payload can never hang.
    if (depth > 6) return '';
    const o = value as Record<string, unknown>;

    // 1. Localised shape (prefer user's browser locale).
    const browserLocale =
      typeof navigator !== 'undefined'
        ? (navigator.language || '').slice(0, 2).toLowerCase()
        : '';
    const orderedLocales = browserLocale
      ? [browserLocale, ...LOCALE_KEYS.filter((k) => k !== browserLocale)]
      : LOCALE_KEYS;
    for (const key of orderedLocales) {
      const v = o[key];
      if (typeof v === 'string' && v.trim()) return v;
      if (typeof v === 'number') return String(v);
    }

    // 2. Direct text-ish keys.
    for (const key of TEXT_KEYS) {
      const v = o[key];
      if (typeof v === 'string' && v.trim()) return v;
      if (typeof v === 'number') return String(v);
      // If the value under the text key is itself an object (e.g. name is
      // { tr, en }), recurse one more level before moving on.
      if (v && typeof v === 'object') {
        const nested = safeText(v, depth + 1);
        if (nested) return nested;
      }
    }

    // 3. Known wrapper keys — recurse one level.
    for (const key of NESTED_KEYS) {
      const v = o[key];
      if (v && typeof v === 'object') {
        const nested = safeText(v, depth + 1);
        if (nested) return nested;
      }
    }

    // 4. Last resort — first primitive leaf anywhere in the object so we
    //    never print "[object Object]".
    for (const v of Object.values(o)) {
      if (typeof v === 'string' && v.trim()) return v;
      if (typeof v === 'number') return String(v);
    }
    // 4b. Still nothing? Try recursing into every nested object (BFS, one
    //     level at a time, capped by depth).
    for (const v of Object.values(o)) {
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        const nested = safeText(v, depth + 1);
        if (nested) return nested;
      }
    }
    return '';
  }
  return '';
};

// Same as safeText but returns a fallback string when the result is empty.
export const safeTextOr = (value: unknown, fallback: string): string => {
  const out = safeText(value);
  return out ? out : fallback;
};

// Defensive number coercion — tolerates `{ value: 5 }`, `{ amount: 5 }`,
// stringified numbers, nulls, and objects with a numeric first value.
export const safeNumber = (value: unknown, fallback = 0): number => {
  if (value == null) return fallback;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    const cand = ['value', 'amount', 'count', 'quantity', 'total', 'price'];
    for (const key of cand) {
      const v = o[key];
      if (typeof v === 'number' && Number.isFinite(v)) return v;
      if (typeof v === 'string') {
        const p = parseFloat(v);
        if (Number.isFinite(p)) return p;
      }
    }
    for (const v of Object.values(o)) {
      if (typeof v === 'number' && Number.isFinite(v)) return v;
    }
  }
  return fallback;
};

// Defensive array unwrap — the backend occasionally wraps a list inside
// a { data: [...] } / { list: [...] } / { items: [...] } envelope. This
// helper always returns a plain array regardless of which shape arrived.
export const coerceArray = <T = unknown>(value: unknown): T[] => {
  if (Array.isArray(value)) return value as T[];
  if (value && typeof value === 'object') {
    const o = value as Record<string, unknown>;
    for (const key of ['data', 'list', 'items', 'rows', 'results']) {
      if (Array.isArray(o[key])) return o[key] as T[];
    }
  }
  return [];
};

export default function QRMenuPage({
  restaurantName,
  restaurantLogo,
  restaurantImage,
  masaName,
  categories,
  products,
  stories,
  cartItems,
  approvedOrders,
  pendingOrders,
  trackingOrders = [],
  trackingLoading = false,
  trackingLastUpdatedAt,
  adisyon = null,
  adisyonLoading = false,
  adisyonError = null,
  adisyonLastUpdatedAt,
  qrCustomer = null,
  qrLifetime = null,
  identifyLoading = false,
  identifyError = null,
  identifyNotFound = false,
  onIdentifyCustomer,
  onForgetCustomer,
  qrHistory = null,
  historyLoading = false,
  historyError = null,
  onLoadHistory,
  activeWaiterCall = null,
  waiterCallLoading = false,
  waiterCallError = null,
  onSubmitWaiterCall,
  onCancelWaiterCall,
  selectedCategory,
  searchQuery,
  cartNotification,
  currency = '₼',
  theme,
  onToggleTheme,
  paymentEnabled = false,
  paymentAmount = 0,
  googlePayConfig,
  isProcessingPayment = false,
  applePayAvailable = false,
  user,
  onUserChange,
  googleClientId,
  appleServiceId,
  onSearch,
  onCategoryChange,
  onAddToCart,
  onUpdateQuantity,
  onRemoveItem,
  onCheckout,
  onCallWaiter,
  onDismissNotification,
  onBackToWelcome,
  onPayOnline,
  onPayGooglePay,
  onPayApplePay,
  onFeedback,
}: QRMenuPageProps) {
  const { t } = useQrLanguage();
  const island = useDynamicIsland();
  const auth = useCustomerAuth();
  const favorites = useCustomerFavorites();
  const [activeTab, setActiveTab] = useState<Tab>('home');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<QrProduct | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  // ----- Ratings state -----
  // restaurant + waiter rating geçmişi localStorage'da "verilmiş" olarak
  // işaretlenir; UI tekrar görünmez (kullanıcı isteği).
  const [isRestaurantRatingOpen, setIsRestaurantRatingOpen] = useState(false);
  const [restaurantRatingBusy, setRestaurantRatingBusy] = useState(false);
  const [waiterRatingBusy, setWaiterRatingBusy] = useState(false);
  const [ratedStateVersion, setRatedStateVersion] = useState(0); // re-render trigger
  // localStorage helper key'leri:
  const restaurantId = adisyon?.restaurant?.id;
  const lastOrderId = adisyon?.orders?.[0]?.order_id;
  const waiterIdFromCall = activeWaiterCall?.assigned_waiter?.id;
  const restaurantRatedKey = restaurantId
    ? `qr_rated_restaurant_${restaurantId}`
    : null;
  const waiterRatedKey =
    waiterIdFromCall && lastOrderId
      ? `qr_rated_waiter_${waiterIdFromCall}_${lastOrderId}`
      : null;
  const isRestaurantRated = useMemo(() => {
    if (!restaurantRatedKey) return false;
    try {
      return localStorage.getItem(restaurantRatedKey) === '1';
    } catch {
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantRatedKey, ratedStateVersion]);
  const isWaiterRated = useMemo(() => {
    if (!waiterRatedKey) return false;
    try {
      return localStorage.getItem(waiterRatedKey) === '1';
    } catch {
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waiterRatedKey, ratedStateVersion]);
  const markRated = useCallback((key: string) => {
    try {
      localStorage.setItem(key, '1');
    } catch {
      /* ignore */
    }
    setRatedStateVersion((v) => v + 1);
  }, []);
  // Waiter-call reason picker (local UI state — parent owns the actual call)
  const [selectedReason, setSelectedReason] = useState<QrWaiterReasonCode | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [selectedTrackingOrderId, setSelectedTrackingOrderId] = useState<number | null>(null);
  const [tickingRemaining, setTickingRemaining] = useState<number | null>(null);
  const [showItemsList, setShowItemsList] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Store the latest callbacks and map in a ref to avoid invalidating stable callbacks
  const latestProps = useRef({
    onAddToCart,
    onUpdateQuantity,
    setSelectedProduct,
    cartQuantityMap: new Map<string, number>()
  });

  // Build a fast lookup of cart quantities so each ProductCard can render the
  // quantity-counter overlay without prop drilling N items per card.
  const cartQuantityMap = useMemo(() => {
    const map = new Map<string, number>();
    cartItems.forEach((item) => {
      const key = `${item.id}|${item.detail_id || ''}`;
      map.set(key, (map.get(key) || 0) + (item.quantity || 0));
    });
    return map;
  }, [cartItems]);

  useEffect(() => {
    latestProps.current = { onAddToCart, onUpdateQuantity, setSelectedProduct, cartQuantityMap };
  });

  const getProductCartQty = (productId: string | number) => {
    let total = 0;
    cartQuantityMap.forEach((qty, key) => {
      if (key.startsWith(`${productId}|`)) total += qty;
    });
    return total;
  };

  // Stable callbacks for ProductCard to prevent expensive re-renders across the whole list
  const handleCardClick = useCallback((product: QrProduct) => {
    latestProps.current.setSelectedProduct(product);
  }, []);

  const handleCardAddToCart = useCallback((product: QrProduct) => {
    latestProps.current.onAddToCart(product);
  }, []);

  const handleCardQtyChange = useCallback((product: QrProduct, delta: number) => {
    const props = latestProps.current;
    let currentQty = 0;
    props.cartQuantityMap.forEach((qty, key) => {
      if (key.startsWith(`${product.id}|`)) currentQty += qty;
    });
    const newQty = Math.max(0, currentQty + delta);
    if (currentQty === 0 && delta > 0) {
      props.onAddToCart(product, delta);
      return;
    }
    props.onUpdateQuantity(product.id, newQty, null);
  }, []);

  // Pick which order to render in the tracking tab. Defaults to the most
  // recently created order; falls back to first if user-selected one is gone.
  const activeTrackingOrder: QrTrackingOrder | null = useMemo(() => {
    if (!trackingOrders.length) return null;
    if (selectedTrackingOrderId != null) {
      const found = trackingOrders.find((o) => o.order_id === selectedTrackingOrderId);
      if (found) return found;
    }
    // newest first (highest id)
    return [...trackingOrders].sort((a, b) => (b.order_id || 0) - (a.order_id || 0))[0];
  }, [trackingOrders, selectedTrackingOrderId]);

  // When the API delivers a fresh `estimated_remaining_seconds` we sync the
  // local ticking countdown so it updates every second between polls without
  // drifting (the next poll will overwrite it with the canonical value).
  useEffect(() => {
    if (!activeTrackingOrder) {
      setTickingRemaining(null);
      return;
    }
    setTickingRemaining(Math.max(0, activeTrackingOrder.estimated_remaining_seconds || 0));
  }, [
    activeTrackingOrder?.order_id,
    activeTrackingOrder?.estimated_remaining_seconds,
    activeTrackingOrder?.current_step,
    trackingLastUpdatedAt,
  ]);

  // Tick the local countdown once per second when there is an active order
  // that is still in the preparing phase.
  useEffect(() => {
    if (!activeTrackingOrder) return;
    if (activeTrackingOrder.is_canceled) return;
    if (activeTrackingOrder.current_step === 'served') return;
    if (tickingRemaining == null || tickingRemaining <= 0) return;
    const id = setInterval(() => {
      setTickingRemaining((prev) => (prev != null && prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [activeTrackingOrder, tickingRemaining]);

  // Auto-jump to tracking tab when an order arrives for the first time so the
  // user doesn't have to hunt for the new "Tracking" tab on small screens.
  const prevTrackingCountRef = useRef(0);
  useEffect(() => {
    if (trackingOrders.length > prevTrackingCountRef.current && trackingOrders.length === 1) {
      // First order placed - keep current tab; user will navigate themselves.
    }
    prevTrackingCountRef.current = trackingOrders.length;
  }, [trackingOrders.length]);

  // Scroll listener — passive + rAF-throttled so iOS Safari can composite the
  // hero/sticky elements on the GPU without being blocked by React updates,
  // and we only flip state when the threshold is actually crossed.
  useEffect(() => {
    let ticking = false;
    const THRESHOLD = 20;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const past = window.scrollY > THRESHOLD;
        setIsScrolled((prev) => (prev === past ? prev : past));
        ticking = false;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Featured products
  const featuredProducts = useMemo(
    () => products.filter((p) => p.isFeatured),
    [products],
  );

  // Filtered products — 'favorites' kategorisi ayrı bir koldan filtrelenir.
  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchesCategory =
        selectedCategory === 'all' ||
        (selectedCategory === 'favorites'
          ? favorites.ids.has(Number(product.id))
          : String(product.category) === String(selectedCategory));
      const q = (searchQuery || '').toLowerCase();
      const matchesSearch = !q || product.name.toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [products, selectedCategory, searchQuery, favorites.ids]);

  // Categories — auth olan + ayarlardan açık + en az 1 favori varsa başa
  // "Bəyəndiyim" kategorisini ekle. Layout'u bozmadan tek yerde yönetilir.
  const displayCategories = useMemo<QrCategory[]>(() => {
    const showFav =
      auth.isAuthenticated &&
      favorites.showFavoritesCategory &&
      favorites.ids.size > 0;
    if (!showFav) return categories;
    const favCat: QrCategory = {
      id: 'favorites',
      name: (t as any).favorites || 'Bəyəndiyim',
    };
    return [favCat, ...categories];
  }, [
    auth.isAuthenticated,
    favorites.showFavoritesCategory,
    favorites.ids,
    categories,
    t,
  ]);

  // ----- Restaurant rating submit -----
  const handleRateRestaurant = useCallback(
    async (rating: number, comment: string) => {
      if (!restaurantId) {
        island.show({
          type: 'error',
          title: 'Restoran tapılmadı',
        });
        return;
      }
      if (!auth.isAuthenticated) {
        island.show({
          type: 'info',
          title: 'Daxil olun',
          message: 'Qiymət vermek üçün hesabınıza daxil olun.',
        });
        setIsRestaurantRatingOpen(false);
        setIsProfileOpen(true);
        return;
      }
      setRestaurantRatingBusy(true);
      try {
        await apiRateRestaurant(restaurantId, {
          rating,
          comment: comment || null,
          order_id: lastOrderId || undefined,
        });
        if (restaurantRatedKey) markRated(restaurantRatedKey);
        setIsRestaurantRatingOpen(false);
        island.show({
          type: 'rating',
          title: 'Təşəkkürlər!',
          message: 'Restorana verdiyiniz qiymət saxlandı.',
        });
      } catch (err: any) {
        island.show({
          type: 'error',
          title:
            err?.response?.data?.message ||
            err?.message ||
            'Qiymət saxlanılmadı',
        });
      } finally {
        setRestaurantRatingBusy(false);
      }
    },
    [
      auth.isAuthenticated,
      island,
      lastOrderId,
      markRated,
      restaurantId,
      restaurantRatedKey,
    ],
  );

  // ----- Waiter rating submit -----
  const handleRateWaiter = useCallback(
    async (rating: number, comment: string) => {
      if (!waiterIdFromCall || !lastOrderId) {
        island.show({
          type: 'error',
          title: 'Ofisant tapılmadı',
          message: 'Sifariş olmadan ofisant qiymətləndirilə bilmir.',
        });
        return;
      }
      if (!auth.isAuthenticated) {
        island.show({
          type: 'info',
          title: 'Daxil olun',
          message: 'Qiymət vermek üçün hesabınıza daxil olun.',
        });
        setIsProfileOpen(true);
        return;
      }
      setWaiterRatingBusy(true);
      try {
        await apiRateWaiter(waiterIdFromCall, {
          rating,
          comment: comment || null,
          order_id: lastOrderId,
        });
        if (waiterRatedKey) markRated(waiterRatedKey);
        island.show({
          type: 'rating',
          title: 'Təşəkkürlər!',
          message: 'Ofisanta verdiyiniz qiymət saxlandı.',
        });
      } catch (err: any) {
        island.show({
          type: 'error',
          title:
            err?.response?.data?.message ||
            err?.message ||
            'Qiymət saxlanılmadı',
        });
      } finally {
        setWaiterRatingBusy(false);
      }
    },
    [auth.isAuthenticated, island, lastOrderId, markRated, waiterIdFromCall, waiterRatedKey],
  );

  // Heart toggle handler — ProductCard'a pass edilir. Auth yoksa Dynamic Island
  // uyarısı gösterir + profil modalını açar; başarılı olunca kısa bildirim atar.
  const handleToggleFavorite = useCallback(
    async (product: QrProduct) => {
      if (!auth.isAuthenticated) {
        island.show({
          type: 'info',
          title: (t as any).favoritesAuthRequired || 'Daxil olun',
          message:
            (t as any).favoritesAuthRequiredMessage ||
            'Favorit yeməklər üçün hesabınıza daxil olun.',
        });
        setIsProfileOpen(true);
        return;
      }
      try {
        const wasFav = favorites.ids.has(Number(product.id));
        const nextState = await favorites.toggle(Number(product.id), {
          name: product.name,
          image: typeof product.image === 'string' ? product.image : null,
          price: Number(product.price) || 0,
        });
        island.show({
          type: 'favorite',
          title:
            nextState
              ? (t as any).favoriteAdded || 'Favoritə əlavə edildi'
              : (t as any).favoriteRemoved || 'Favoritdən çıxarıldı',
          message: product.name,
          duration: 2000,
        });
        // wasFav gölge — yalnızca debug; nextState authoritative.
        void wasFav;
      } catch (err: any) {
        island.show({
          type: 'error',
          title:
            (t as any).favoritesError || 'Favorit yenilənmədi',
          message: err?.response?.data?.message || err?.message,
        });
        throw err;
      }
    },
    [auth.isAuthenticated, favorites, island, t],
  );

  const cartTotal = cartItems.reduce(
    (sum, item) => sum + parseFloat(String(item.price)) * item.quantity,
    0,
  );
  const cartItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const heroImg = restaurantImage
    ? restaurantImage.startsWith('http')
      ? restaurantImage
      : `${img_url}/${restaurantImage}`
    : HERO_DEFAULT;
  const logoUrl = getImg(restaurantLogo);

  const switchTab = (tab: Tab) => {
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalAdisyon =
    paymentAmount > 0
      ? paymentAmount
      : approvedOrders.reduce((acc, o) => acc + (o.total_price || 0), 0);

  // ===== QR Adisyon (live bill) derived state =====
  // Prefer the live `adisyon` snapshot whenever it is available; everything
  // else (sums, currency symbol, item lists) is computed from it. When the
  // snapshot is missing we keep the legacy `approvedOrders/pendingOrders`
  // fallback so the screen never appears empty during the first paint.
  const billCurrency = safeTextOr(adisyon?.totals?.currency, currency);
  const billTotals = adisyon?.totals || null;
  const billOrders = coerceArray<typeof adisyon extends { orders: Array<infer U> } ? U : never>(
    adisyon?.orders as unknown,
  );
  const billRestaurant = adisyon?.restaurant;
  const billTable = adisyon?.table;

  // Local phone-input state for the "tell us your phone" form. Kept here so
  // the parent only deals with API-shaped data (phone strings).
  const [phoneInput, setPhoneInput] = useState('');
  const [historyExpanded, setHistoryExpanded] = useState(false);

  // Auto-load the first page of history the first time the user expands it.
  useEffect(() => {
    if (historyExpanded && qrCustomer && !qrHistory && !historyLoading && onLoadHistory) {
      onLoadHistory(1, 10);
    }
  }, [historyExpanded, qrCustomer, qrHistory, historyLoading, onLoadHistory]);

  const handlePhoneSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = phoneInput.trim();
    if (!trimmed || !onIdentifyCustomer) return;
    onIdentifyCustomer(trimmed);
  };

  // Localised name for kitchen status (used as a small badge on each item).
  const kitchenStatusLabel = (status: string | null | undefined): string => {
    switch (status) {
      case 'ready':
        return t.kitchenStatusReady;
      case 'preparing':
        return t.kitchenStatusPreparing;
      case 'pending':
      default:
        return t.kitchenStatusPending;
    }
  };
  const kitchenStatusClasses = (status: string | null | undefined): string => {
    switch (status) {
      case 'ready':
        return 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400';
      case 'preparing':
        return 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400';
      case 'pending':
      default:
        return 'bg-gray-100 text-gray-500 dark:bg-white/5 dark:text-gray-400';
    }
  };

  // Localised label for a payment-method enum value used in history rows.
  const paymentMethodLabel = (method: string): string => {
    switch (method) {
      case 'cash':
        return t.paymentMethodCash;
      case 'bank':
        return t.paymentMethodBank;
      case 'customer_balance':
        return t.paymentMethodCustomerBalance;
      default:
        return method;
    }
  };

  // Compact "5 dəq əvvəl" / "just now" style badge for the live indicator.
  const formatLiveAgo = (ts?: number): string => {
    if (!ts) return '';
    const diff = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (diff < 5) return t.liveSyncedNow;
    if (diff < 60) return `${diff}s`;
    const m = Math.floor(diff / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    return `${h}h`;
  };

  // Format a date into a friendly local form for history rows.
  const formatDate = (iso: string | null | undefined): string => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  // ===== Waiter-call helpers =====
  // Reason metadata used by the picker grid. Order matches the spec — water,
  // bill, help, cleanup, menu, other — so backend canonical codes line up.
  const reasons: Array<{
    code: QrWaiterReasonCode;
    label: string;
    icon: typeof Droplets;
    gradient: string; // when selected
    shadow: string;
    iconBg: string; // when not selected
    iconColor: string;
  }> = [
      {
        code: 'water',
        label: t.waiterCallReasonWater,
        icon: Droplets,
        gradient: 'from-cyan-500 to-blue-500',
        shadow: 'shadow-cyan-500/40',
        iconBg: 'from-cyan-50 to-blue-50 dark:from-cyan-500/20 dark:to-blue-500/10',
        iconColor: 'text-cyan-600 dark:text-cyan-400',
      },
      {
        code: 'bill',
        label: t.waiterCallReasonBill,
        icon: Receipt,
        gradient: 'from-orange-500 to-amber-500',
        shadow: 'shadow-orange-500/40',
        iconBg: 'from-orange-50 to-amber-50 dark:from-orange-500/20 dark:to-amber-500/10',
        iconColor: 'text-orange-600 dark:text-orange-400',
      },
      {
        code: 'help',
        label: t.waiterCallReasonHelp,
        icon: HelpCircle,
        gradient: 'from-violet-500 to-purple-500',
        shadow: 'shadow-violet-500/40',
        iconBg: 'from-violet-50 to-purple-50 dark:from-violet-500/20 dark:to-purple-500/10',
        iconColor: 'text-violet-600 dark:text-violet-400',
      },
      {
        code: 'cleanup',
        label: t.waiterCallReasonCleanup,
        icon: Sparkles,
        gradient: 'from-emerald-500 to-teal-500',
        shadow: 'shadow-emerald-500/40',
        iconBg: 'from-emerald-50 to-teal-50 dark:from-emerald-500/20 dark:to-teal-500/10',
        iconColor: 'text-emerald-600 dark:text-emerald-400',
      },
      {
        code: 'menu',
        label: t.waiterCallReasonMenu,
        icon: BookOpen,
        gradient: 'from-pink-500 to-rose-500',
        shadow: 'shadow-pink-500/40',
        iconBg: 'from-pink-50 to-rose-50 dark:from-pink-500/20 dark:to-rose-500/10',
        iconColor: 'text-pink-600 dark:text-pink-400',
      },
      {
        code: 'other',
        label: t.waiterCallReasonOther,
        icon: Pencil,
        gradient: 'from-slate-500 to-gray-600',
        shadow: 'shadow-slate-500/40',
        iconBg: 'from-gray-50 to-slate-50 dark:from-gray-500/20 dark:to-slate-500/10',
        iconColor: 'text-gray-600 dark:text-gray-400',
      },
    ];
  // Sanity-check: keep this list in lock-step with the backend canonical codes.
  // (compile-time only — dropped by the bundler in production)
  void QR_WAITER_REASON_CODES;

  // "Called X ago" badge for the active call card. Uses the localised time
  // strings so we don't hard-code Turkish/English suffixes.
  const formatCallAgo = (iso: string | null | undefined): string => {
    if (!iso) return '';
    let ts: number;
    try {
      ts = new Date(iso).getTime();
    } catch {
      return '';
    }
    if (!Number.isFinite(ts)) return '';
    const diff = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (diff < 5) return t.waiterCallTimeJustNow;
    if (diff < 60) return t.waiterCallTimeSeconds(diff);
    const m = Math.floor(diff / 60);
    if (m < 60) return t.waiterCallTimeMinutes(m);
    const h = Math.floor(m / 60);
    return t.waiterCallTimeHours(h);
  };

  // Reset the picker after a successful submit / cancel from the parent.
  // We watch `activeWaiterCall` going from null -> populated to clear local
  // state so the next time the customer opens the picker it starts fresh.
  useEffect(() => {
    if (activeWaiterCall) {
      setSelectedReason(null);
      setNoteText('');
      setNoteOpen(false);
    }
  }, [activeWaiterCall]);

  return (
    <div
      ref={containerRef}
      className={`min-h-screen bg-[#F8F9FA] dark:bg-[#09090b] relative pb-28 font-sans selection:bg-orange-500 selection:text-white qrm-container`}
    >
      {/* ============ HOME TAB ============ */}
      {activeTab === 'home' && (
        <>
          {/* Sticky Search Header — borderless, no background flip on scroll.
              Over the hero it stays transparent; once scrolled past the hero
              it gets a subtle blurred background for readability, but never
              shows a divider line. */}
          <div
            className={`sticky top-0 z-40 transition-colors duration-300 ${isScrolled
              ? 'bg-[#F8F9FA]/85 dark:bg-[#09090b]/85 backdrop-blur-2xl'
              : 'bg-transparent'
              }`}
          >
            <div className="flex items-center gap-3 px-5 py-3 qrm-safe-top">
              <button
                type="button"
                onClick={onBackToWelcome}
                className={`w-11 h-11 rounded-2xl flex items-center justify-center hover:scale-105 active:scale-95 transition-all ${isScrolled
                  ? 'bg-gray-100 dark:bg-white/5 shadow-sm'
                  : 'bg-black/30 backdrop-blur-xl border border-white/20'
                  }`}
                aria-label="back"
              >
                <ChevronLeft
                  className={`w-5 h-5 ${isScrolled ? 'text-gray-900 dark:text-white' : 'text-white'
                    }`}
                />
              </button>

              <div className="flex-1 relative group">
                <Search
                  className={`absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] transition-colors z-10 ${isScrolled ? 'text-gray-400' : 'text-white/70'
                    }`}
                />
                <input
                  type="text"
                  placeholder={t.search}
                  value={searchQuery}
                  onChange={(e) => onSearch(e.target.value)}
                  className={`w-full pl-11 pr-10 py-3 rounded-2xl text-[15px] outline-none font-medium transition-all focus:ring-2 focus:ring-orange-500/30 ${isScrolled
                    ? 'bg-gray-100 dark:bg-white/5 border border-transparent dark:border-white/5 shadow-sm placeholder:text-gray-400 dark:placeholder:text-gray-500 text-gray-900 dark:text-white'
                    : 'bg-white/15 backdrop-blur-xl border border-white/20 placeholder:text-white/70 text-white'
                    }`}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => onSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    <X
                      className={`w-4 h-4 ${isScrolled ? 'text-gray-400' : 'text-white/70'
                        }`}
                    />
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => setIsProfileOpen(true)}
                className={`w-11 h-11 rounded-2xl flex items-center justify-center hover:scale-105 active:scale-95 transition-all ${isScrolled
                  ? 'bg-gray-100 dark:bg-white/5 shadow-sm'
                  : 'bg-black/30 backdrop-blur-xl border border-white/20'
                  }`}
                aria-label="profile"
              >
                <User
                  className={`w-5 h-5 ${isScrolled ? 'text-gray-900 dark:text-white' : 'text-white'
                    }`}
                />
              </button>
            </div>
          </div>

          {/* Hero with restaurant image */}
          <div className="relative h-[320px] overflow-visible -mt-[68px] pt-[68px]">
            <div className="absolute inset-0 overflow-hidden">
              <img
                src={heroImg}
                alt={restaurantName || ''}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/85" />
            </div>

            <div className="absolute bottom-0 left-0 right-0 z-10 flex flex-col items-center text-center px-5 pb-8 pt-[68px]">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={restaurantName || ''}
                  className="w-14 h-14 rounded-2xl bg-orange-500 p-1 mb-2 object-cover shadow-2xl"
                />
              ) : (
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center mb-2 shadow-2xl shadow-orange-500/40">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
              )}

              <h1 className="text-[44px] font-black tracking-tight text-white mb-3 drop-shadow-2xl line-clamp-1">
                {restaurantName || 'Rezmo'}
              </h1>

              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 backdrop-blur-xl border border-white/20 shadow-lg">
                <Clock className="w-3 h-3 text-white/80" />
                <span className="text-white text-[11px] font-bold">{t.openUntil}</span>
              </div>
            </div>

            {/* Curved bottom edge */}
            <div className="absolute -bottom-1 left-0 right-0 h-10 overflow-hidden pointer-events-none">
              <svg
                viewBox="0 0 375 50"
                className="w-full h-full"
                preserveAspectRatio="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M 0 50 Q 187.5 0 375 50 L 375 50 L 0 50 Z"
                  className="fill-[#F8F9FA] dark:fill-[#09090b]"
                />
              </svg>
            </div>
          </div>

          {/* Stories */}
          {stories.length > 0 && (
            <div className="bg-transparent pt-2 pb-2">
              <MenuStories stories={stories} />
            </div>
          )}

          {/* Categories — displayCategories içinde 'Bəyəndiyim' kategorisi de
              auth + ayarlar + favori sayısına göre otomatik eklenir. */}
          <CategoryNav
            categories={displayCategories}
            selectedCategory={selectedCategory}
            onCategoryChange={onCategoryChange}
            isScrolled={isScrolled}
          />

          <div className="px-5 py-6">
            {/* Featured */}
            {!searchQuery && featuredProducts.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-orange-500" />
                    {t.chefRecommendations}
                  </h2>
                </div>
                <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-4 -mx-5 px-5">
                  {featuredProducts.map((product) => (
                    <div key={product.id} className="w-[260px] shrink-0">
                      <ProductCard
                        product={product}
                        onAddToCart={handleCardAddToCart}
                        onClick={handleCardClick}
                        isLarge
                        currency={currency}
                        cartQuantity={getProductCartQty(product.id)}
                        onQuantityChange={handleCardQtyChange}
                        isFavorited={favorites.ids.has(Number(product.id))}
                        onToggleFavorite={handleToggleFavorite}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Products grid */}
            <div>
              <h2 className="text-lg font-bold tracking-tight text-gray-900 dark:text-white mb-4">
                {selectedCategory === 'all'
                  ? t.allFlavors
                  : selectedCategory === 'favorites'
                    ? (t as any).favorites || 'Bəyəndiyim'
                    : categories.find((c) => String(c.id) === String(selectedCategory))?.name ||
                    t.flavors}
              </h2>

              {filteredProducts.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 dark:text-gray-400 text-lg font-medium">
                    {t.productsNotFound}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 pb-24">
                  {filteredProducts.map((product) => (
                    <ProductCard
                      key={`${product.id}`}
                      product={product}
                      onAddToCart={handleCardAddToCart}
                      onClick={handleCardClick}
                      currency={currency}
                      cartQuantity={getProductCartQty(product.id)}
                      onQuantityChange={handleCardQtyChange}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ============ TRACKING TAB ============ */}
      {activeTab === 'tracking' && (
        <div className="px-5 py-8 pb-32 min-h-[60vh] animate-fade-in qrm-safe-top">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => switchTab('home')}
                className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-white/5 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-900 dark:text-white" />
              </button>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t.orderTracking}
              </h2>
            </div>
            {trackingLoading && trackingOrders.length > 0 && (
              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 dark:text-gray-400 px-2.5 py-1 rounded-full bg-gray-100 dark:bg-white/5">
                <RefreshCw className="w-3 h-3 animate-spin" />
                <span>{t.refreshing}</span>
              </div>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 ml-11">
            {t.orderStatus}
          </p>

          {/* Empty state — no active orders for this table */}
          {!activeTrackingOrder ? (
            <div className="bg-white dark:bg-[#1a1a1a] rounded-[24px] p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.2)] border border-gray-100 dark:border-white/5 text-center">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-500/10 dark:to-amber-500/10 flex items-center justify-center mb-5">
                <Hourglass className="w-9 h-9 text-orange-500/70 dark:text-orange-400/70" />
              </div>
              <h3 className="text-lg font-extrabold text-gray-900 dark:text-white mb-2">
                {t.noActiveOrderTrack}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
                {t.noActiveOrderTrackDesc}
              </p>
              <button
                type="button"
                onClick={() => switchTab('home')}
                className="inline-flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-2xl font-bold text-sm shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 active:scale-95 transition-all"
              >
                {t.goToMenu}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              {/* Multi-order switcher (only when more than one active order) */}
              {trackingOrders.length > 1 && (
                <div className="mb-4">
                  <h4 className="text-[11px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2 ml-1">
                    {t.yourOrders}
                  </h4>
                  <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-5 px-5 pb-1">
                    {[...trackingOrders]
                      .sort((a, b) => (b.order_id || 0) - (a.order_id || 0))
                      .map((order) => {
                        const isSelected = order.order_id === activeTrackingOrder.order_id;
                        const orderNoLabel = safeTextOr(order.order_number, `#${order.order_id}`);
                        const accentClass = order.is_canceled
                          ? 'border-red-400 text-red-500 bg-red-50 dark:bg-red-500/10'
                          : order.current_step === 'served'
                            ? 'border-green-400 text-green-600 bg-green-50 dark:bg-green-500/10'
                            : order.current_step === 'ready'
                              ? 'border-blue-400 text-blue-600 bg-blue-50 dark:bg-blue-500/10'
                              : order.status === 'pending_approval'
                                ? 'border-amber-400 text-amber-600 bg-amber-50 dark:bg-amber-500/10'
                                : 'border-orange-400 text-orange-600 bg-orange-50 dark:bg-orange-500/10';
                        return (
                          <button
                            key={order.order_id}
                            type="button"
                            onClick={() => setSelectedTrackingOrderId(order.order_id)}
                            className={`shrink-0 px-4 py-2 rounded-2xl border-2 text-[13px] font-bold transition-all ${isSelected
                              ? `${accentClass} shadow-md scale-105`
                              : 'border-gray-200 dark:border-white/10 text-gray-500 dark:text-gray-400 bg-white dark:bg-[#1a1a1a]'
                              }`}
                          >
                            {orderNoLabel}
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Canceled banner */}
              {activeTrackingOrder.is_canceled && (
                <div className="mb-4 bg-red-50 dark:bg-red-500/10 border-2 border-red-200 dark:border-red-500/20 rounded-2xl p-4 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-500 text-white flex items-center justify-center shrink-0">
                    <AlertCircle className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-extrabold text-red-700 dark:text-red-300">
                      {t.orderCanceled}
                    </h4>
                    <p className="text-[12px] text-red-600/80 dark:text-red-400/80 mt-0.5">
                      {t.orderCanceledDesc}
                    </p>
                  </div>
                </div>
              )}

              {/* Main tracking card */}
              {(() => {
                const order = activeTrackingOrder;
                const step = order.is_canceled ? 'canceled' : order.current_step;
                const isPending = order.status === 'pending_approval' && !order.is_canceled;

                // State-driven visual theming
                const theme = order.is_canceled
                  ? {
                    ring: 'border-red-300/60 dark:border-red-500/30',
                    barFill: 'bg-red-500',
                    barWidth: 'w-full',
                    headerColor: 'text-red-500',
                    iconBg: 'bg-red-50 dark:bg-red-500/10 text-red-500',
                    icon: <AlertCircle className="w-6 h-6" />,
                  }
                  : step === 'served'
                    ? {
                      ring: 'border-green-500/50 ring-2 ring-green-500/20',
                      barFill: 'bg-green-500',
                      barWidth: 'w-full',
                      headerColor: 'text-green-500',
                      iconBg: 'bg-green-50 dark:bg-green-500/10 text-green-500',
                      icon: <CheckCircle2 className="w-6 h-6" />,
                    }
                    : step === 'ready'
                      ? {
                        ring: 'border-blue-400/50 ring-2 ring-blue-500/20',
                        barFill: 'bg-blue-500',
                        barWidth: 'w-[85%]',
                        headerColor: 'text-blue-500',
                        iconBg: 'bg-blue-50 dark:bg-blue-500/10 text-blue-500',
                        icon: <CheckCircle2 className="w-6 h-6" />,
                      }
                      : isPending
                        ? {
                          ring: 'border-amber-300/60 dark:border-amber-500/20',
                          barFill: 'bg-amber-500',
                          barWidth: 'w-[15%]',
                          headerColor: 'text-amber-500',
                          iconBg:
                            'bg-amber-50 dark:bg-amber-500/10 text-amber-500',
                          icon: <Hourglass className="w-6 h-6 animate-pulse" />,
                        }
                        : {
                          ring: 'border-gray-100 dark:border-white/5',
                          barFill: 'bg-orange-500',
                          barWidth: 'w-1/2',
                          headerColor: 'text-orange-500',
                          iconBg:
                            'bg-orange-50 dark:bg-orange-500/10 text-orange-500',
                          icon: <ChefHat className="w-6 h-6 animate-pulse" />,
                        };

                // Items progress (kitchen progress)
                const summary = order.items_summary || {
                  total: 0,
                  ready: 0,
                  preparing: 0,
                  pending: 0,
                };
                const progressPct =
                  summary.total > 0
                    ? Math.round((summary.ready / summary.total) * 100)
                    : 0;

                // Header subtitle: localized current step label
                const headerLabel = order.is_canceled
                  ? t.orderCanceled
                  : step === 'served'
                    ? t.servedDone
                    : step === 'ready'
                      ? t.readyLabel
                      : isPending
                        ? t.pendingApprovalLabel
                        : t.preparingInKitchen;

                const headerSubtext = order.is_canceled
                  ? null
                  : step === 'served'
                    ? t.enjoy
                    : step === 'ready'
                      ? t.readyDesc
                      : isPending
                        ? t.pendingApprovalDesc
                        : null;

                // Countdown formatter
                const remaining = tickingRemaining ?? order.estimated_remaining_seconds ?? 0;
                const showCountdown =
                  !order.is_canceled &&
                  step !== 'served' &&
                  step !== 'ready' &&
                  !isPending;
                const countdownText = (() => {
                  if (remaining <= 0) return t.almostReady;
                  const m = Math.floor(remaining / 60);
                  const s = remaining % 60;
                  return `${m}:${String(s).padStart(2, '0')}`;
                })();

                return (
                  <div
                    className={`bg-white dark:bg-[#1a1a1a] rounded-[24px] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.2)] border ${theme.ring} relative overflow-hidden transition-all`}
                  >
                    {/* Top progress bar */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gray-100 dark:bg-white/5">
                      <div
                        className={`h-full ${theme.barFill} ${theme.barWidth} rounded-r-full ${step === 'preparing' && !order.is_canceled
                          ? 'animate-shimmer-bar'
                          : ''
                          } transition-all`}
                      />
                    </div>

                    {/* Header */}
                    <div className="flex items-center justify-between mb-5 pt-2">
                      <div className="flex-1 min-w-0">
                        <span
                          className={`text-[12px] font-bold uppercase tracking-wider mb-1 block ${theme.headerColor}`}
                        >
                          {t.orderNumber}: {safeTextOr(order.order_number, `#${order.order_id}`)}
                        </span>
                        <h3 className="text-lg font-extrabold text-gray-900 dark:text-white truncate">
                          {safeText(headerLabel)}
                        </h3>
                        {showCountdown && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 font-medium flex items-center gap-1">
                            <Timer className="w-3.5 h-3.5" />
                            {t.estimated}: {countdownText}
                          </p>
                        )}
                        {!showCountdown && headerSubtext && (
                          <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-1 font-medium">
                            {headerSubtext}
                          </p>
                        )}
                      </div>
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${theme.iconBg}`}
                      >
                        {theme.icon}
                      </div>
                    </div>

                    {/* Items progress bar (kitchen progress) */}
                    {!order.is_canceled && summary.total > 0 && (
                      <div className="mb-5 p-3 rounded-2xl bg-gray-50 dark:bg-white/5">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <ChefHat className="w-3.5 h-3.5" />
                            {t.itemsProgress}
                          </span>
                          <span className="text-[12px] font-extrabold text-gray-900 dark:text-white">
                            {safeNumber(summary.ready)}/{safeNumber(summary.total)}
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-gray-200 dark:bg-white/10 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-700 ${theme.barFill}`}
                            style={{ width: `${progressPct}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Timeline (3 visible steps: received → preparing → served) */}
                    {!order.is_canceled && (
                      <div className="space-y-4 relative mb-5">
                        {(() => {
                          // Filter to the 3 visible steps and ensure order
                          const VISIBLE: QrTrackingStep[] = [
                            'received',
                            'preparing',
                            'served',
                          ];
                          // Rank-based progress lets us decide which steps are
                          // before / at / after the current step without running
                          // into TypeScript's control-flow narrowing on string
                          // literal comparisons.
                          const STEP_RANK: Record<QrTrackingStep, number> = {
                            canceled: -1,
                            received: 0,
                            preparing: 1,
                            ready: 2,
                            served: 3,
                          };
                          const orderRank = STEP_RANK[step] ?? 0;
                          const byStep = new Map(
                            order.timeline.map((s) => [s.step, s] as const),
                          );
                          return VISIBLE.map((stepKey, idx) => {
                            const tlItem = byStep.get(stepKey);
                            // Synthesize defaults if backend didn't return that step
                            const labelMap: Record<string, string> = {
                              received: t.orderReceived,
                              preparing: t.preparing,
                              served: t.served,
                            };
                            const label = tlItem?.label || labelMap[stepKey];
                            const isLast = idx === VISIBLE.length - 1;
                            const myRank = STEP_RANK[stepKey];

                            // The "ready" sub-state collapses into the visible
                            // "preparing" timeline node, so when current_step is
                            // 'ready' the preparing node is still considered the
                            // active row (and 'received' before it is completed).
                            const completed =
                              tlItem?.completed ?? orderRank > myRank;

                            const isCurrent =
                              tlItem?.is_current ??
                              (stepKey === step ||
                                (stepKey === 'preparing' && step === 'ready'));

                            // Time label
                            const timeStr = tlItem?.occurred_at
                              ? new Date(tlItem.occurred_at).toLocaleTimeString(
                                [],
                                { hour: '2-digit', minute: '2-digit' },
                              )
                              : tlItem?.estimated_at
                                ? `${t.estimated}: ${new Date(
                                  tlItem.estimated_at,
                                ).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}`
                                : null;

                            // Visuals
                            const dotClass = completed
                              ? 'bg-green-500 text-white shadow shadow-green-500/30'
                              : isCurrent
                                ? `bg-white dark:bg-black border-2 ${step === 'ready'
                                  ? 'border-blue-500 text-blue-500'
                                  : 'border-orange-500 text-orange-500'
                                }`
                                : 'bg-gray-200 dark:bg-white/10 border-2 border-white dark:border-[#1a1a1a]';

                            const cardClass = completed
                              ? 'bg-green-50 dark:bg-green-500/10 border border-green-100 dark:border-green-500/20'
                              : isCurrent
                                ? step === 'ready'
                                  ? 'bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-500/20'
                                  : 'bg-orange-50 dark:bg-orange-500/10 border border-orange-100 dark:border-orange-500/20'
                                : 'bg-gray-50 dark:bg-white/5 opacity-60';

                            const subColor = completed
                              ? 'text-green-600 dark:text-green-400'
                              : isCurrent
                                ? step === 'ready'
                                  ? 'text-blue-600 dark:text-blue-400'
                                  : 'text-orange-600 dark:text-orange-400'
                                : 'text-gray-500';

                            return (
                              <div key={stepKey} className="relative flex items-start gap-4">
                                {/* Connector line */}
                                {!isLast && (
                                  <div className="absolute left-3 top-7 bottom-[-12px] w-px bg-gradient-to-b from-gray-200 to-transparent dark:from-white/10 z-0" />
                                )}
                                <div
                                  className={`flex items-center justify-center w-6 h-6 rounded-full z-10 shrink-0 mt-1 ${dotClass}`}
                                >
                                  {completed ? (
                                    <CheckCircle2 className="w-4 h-4" />
                                  ) : isCurrent ? (
                                    <div
                                      className={`w-2 h-2 rounded-full animate-ping ${step === 'ready' ? 'bg-blue-500' : 'bg-orange-500'
                                        }`}
                                    />
                                  ) : null}
                                </div>
                                <div className={`flex-1 p-3 rounded-xl ${cardClass}`}>
                                  <h4 className="text-[14px] font-bold text-gray-900 dark:text-white">
                                    {safeText(label)}
                                  </h4>
                                  <span className={`text-[11px] ${subColor}`}>
                                    {completed && timeStr
                                      ? timeStr
                                      : isCurrent
                                        ? stepKey === 'served' && step === 'ready'
                                          ? t.readyNow
                                          : t.rightNow
                                        : timeStr || ''}
                                  </span>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}

                    {/* Items list (collapsible).
                        coerceArray defends against `items` arriving as { data:[] }
                        or { stocks: [...], stock_sets: [...] } wrappers. */}
                    {(() => {
                      const itemsList = coerceArray<typeof order.items[0]>(order.items as unknown);
                      if (itemsList.length === 0) return null;
                      return (
                        <div className="mb-4 border-t border-gray-100 dark:border-white/5 pt-4">
                          <button
                            type="button"
                            onClick={() => setShowItemsList((v) => !v)}
                            className="w-full flex items-center justify-between py-1 hover:opacity-80 transition-opacity"
                          >
                            <span className="text-[13px] font-bold text-gray-900 dark:text-white flex items-center gap-2">
                              <Package className="w-4 h-4 text-gray-500" />
                              {t.itemsList} ({itemsList.length})
                            </span>
                            {showItemsList ? (
                              <ChevronUp className="w-4 h-4 text-gray-500" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-gray-500" />
                            )}
                          </button>
                          {showItemsList && (
                            <div className="mt-3 space-y-2">
                              {itemsList.map((item, idx) => {
                                // Visual theme for the quantity pill + card — keyed on
                                // kitchen status so the customer can skim a long list
                                // and instantly see what's ready vs still cooking.
                                const stateTheme =
                                  item.kitchen_status === 'ready'
                                    ? {
                                      qty: 'bg-green-500 text-white shadow-sm shadow-green-500/30',
                                      card: 'border-green-100 dark:border-green-500/20 bg-green-50/60 dark:bg-green-500/5',
                                      pill: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300',
                                      icon: <CheckCircle2 className="w-3 h-3" />,
                                    }
                                    : item.kitchen_status === 'preparing'
                                      ? {
                                        qty: 'bg-orange-500 text-white shadow-sm shadow-orange-500/30',
                                        card: 'border-orange-100 dark:border-orange-500/20 bg-orange-50/60 dark:bg-orange-500/5',
                                        pill: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300',
                                        icon: <ChefHat className="w-3 h-3" />,
                                      }
                                      : {
                                        qty: 'bg-gray-200 dark:bg-white/10 text-gray-600 dark:text-gray-300',
                                        card: 'border-gray-100 dark:border-white/5 bg-gray-50/60 dark:bg-white/[0.02]',
                                        pill: 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300',
                                        icon: <Hourglass className="w-3 h-3" />,
                                      };
                                const itemStateLabel =
                                  item.kitchen_status === 'ready'
                                    ? t.itemReady
                                    : item.kitchen_status === 'preparing'
                                      ? t.itemPreparing
                                      : t.itemPending;
                                const itemName = safeTextOr(item.name, '—');
                                const isSet = item.type === 'stock_set';
                                return (
                                  <div
                                    key={`tracking-item-${idx}`}
                                    className={`flex items-center gap-3 p-2.5 rounded-2xl border ${stateTheme.card} transition-colors`}
                                  >
                                    {/* Quantity chip */}
                                    <div
                                      className={`w-11 h-11 rounded-xl shrink-0 flex items-center justify-center font-black text-[14px] leading-none ${stateTheme.qty}`}
                                    >
                                      {safeNumber(item.quantity, 1)}×
                                    </div>

                                    {/* Name + set tag */}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[14px] font-bold text-gray-900 dark:text-white truncate leading-tight">
                                        {itemName}
                                      </p>
                                      {isSet && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-black text-purple-600 dark:text-purple-400 uppercase tracking-wider mt-0.5">
                                          <Sparkles className="w-3 h-3" />
                                          {t.setMenu}
                                        </span>
                                      )}
                                    </div>

                                    {/* Status pill */}
                                    <span
                                      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shrink-0 ${stateTheme.pill}`}
                                    >
                                      {stateTheme.icon}
                                      {itemStateLabel}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* View order details button */}
                    <div className="border-t border-gray-100 dark:border-white/5 pt-4">
                      <button
                        type="button"
                        onClick={() => switchTab('history')}
                        className="w-full py-3.5 bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 text-gray-900 dark:text-white rounded-xl text-[14px] font-bold transition-colors flex items-center justify-center gap-2"
                      >
                        {t.viewOrderDetails}
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* ============ WAITER TAB ============ */}
      {activeTab === 'waiter' && (
        <div className="px-5 py-8 pb-32 min-h-[60vh] animate-fade-in qrm-safe-top">
          {/* Title row */}
          <div className="flex items-center gap-2 mb-2">
            <button
              type="button"
              onClick={() => switchTab('home')}
              className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-white/5 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-900 dark:text-white" />
            </button>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t.waiterCallHero}
            </h2>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 ml-11">
            {t.waiterCallHeroDesc}
          </p>

          {/* Live indicator + error toast */}
          <div className="ml-11 mb-5 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[11px] font-bold">
              {waiterCallLoading ? (
                <RefreshCw className="w-3 h-3 animate-spin" />
              ) : (
                <Wifi className="w-3 h-3" />
              )}
              {t.waiterCallLiveBadge}
            </span>
            {waiterCallError && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 text-[11px] font-bold max-w-[80%] truncate">
                <AlertCircle className="w-3 h-3 shrink-0" />
                <span className="truncate">{waiterCallError}</span>
              </span>
            )}
          </div>

          {/* ============================================================
                ACTIVE CALL CARD (pending / acknowledged)
              ============================================================ */}
          {activeWaiterCall ? (
            <div
              className={`relative rounded-[28px] p-6 mb-6 overflow-hidden shadow-2xl text-white ${activeWaiterCall.status === 'acknowledged'
                ? 'bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 shadow-emerald-500/40'
                : 'bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 shadow-orange-500/40'
                }`}
            >
              {/* Decorative blur shapes */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />

              <div className="relative z-10">
                {/* Status icon + title + description */}
                <div className="flex items-start gap-4 mb-5">
                  <div
                    className={`w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center shrink-0 ring-2 ring-white/30 ${activeWaiterCall.status === 'pending' ? 'animate-pulse' : ''
                      }`}
                  >
                    {activeWaiterCall.status === 'pending' ? (
                      <BellRing className="w-8 h-8" />
                    ) : (
                      <CheckCircle2 className="w-8 h-8" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-black leading-tight mb-1">
                      {activeWaiterCall.status === 'acknowledged'
                        ? t.waiterCallStatusAcknowledged
                        : t.waiterCallStatusPending}
                    </h3>
                    <p className="text-sm opacity-90 leading-snug">
                      {activeWaiterCall.status === 'acknowledged'
                        ? t.waiterCallStatusAcknowledgedDesc
                        : t.waiterCallStatusPendingDesc}
                    </p>
                  </div>
                </div>

                {/* Reason + assigned waiter + time pills */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {activeWaiterCall.reason_label && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-md text-[12px] font-bold">
                      <Tag className="w-3 h-3" />
                      {activeWaiterCall.reason_label}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-md text-[12px] font-bold max-w-full">
                    <User className="w-3 h-3 shrink-0" />
                    <span className="truncate">
                      {activeWaiterCall.assigned_waiter
                        ? t.waiterCallNotifiedWho(activeWaiterCall.assigned_waiter.name)
                        : t.waiterCallNoAssigned}
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-md text-[12px] font-bold">
                    <Clock className="w-3 h-3" />
                    {t.waiterCallCreatedAgo(formatCallAgo(activeWaiterCall.created_at))}
                  </span>
                </div>

                {/* Optional note shown back to the customer */}
                {activeWaiterCall.note && (
                  <div className="bg-white/15 backdrop-blur-md rounded-2xl p-3 mb-4 border border-white/10">
                    <p className="text-[10px] font-bold uppercase tracking-wider opacity-80 mb-0.5">
                      {t.waiterCallNoteShown}
                    </p>
                    <p className="text-[13px] font-medium leading-snug break-words">
                      {activeWaiterCall.note}
                    </p>
                  </div>
                )}

                {/* Cancel button — only while still pending */}
                {activeWaiterCall.status === 'pending' && onCancelWaiterCall && (
                  <button
                    type="button"
                    onClick={() => onCancelWaiterCall()}
                    disabled={waiterCallLoading}
                    className="w-full bg-white/15 hover:bg-white/25 backdrop-blur-md border border-white/20 px-4 py-3 rounded-2xl text-[14px] font-bold transition-all active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {waiterCallLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t.waiterCallCancelling}
                      </>
                    ) : (
                      <>
                        <X className="w-4 h-4" />
                        {t.waiterCallCancelByCustomer}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          ) : (
            /* ============================================================
                  REASON PICKER (no active call)
                ============================================================ */
            <div className="bg-white dark:bg-[#1a1a1a] rounded-[28px] p-5 mb-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.2)] border border-gray-100 dark:border-white/5">
              {/* Header row */}
              <div className="flex items-center gap-3 mb-1">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white flex items-center justify-center shadow-md shadow-orange-500/30 shrink-0">
                  <BellRing className="w-5 h-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-extrabold text-gray-900 dark:text-white text-[15px] leading-tight">
                    {t.waiterCallReasonTitle}
                  </h3>
                  <p className="text-[12px] text-gray-500 dark:text-gray-400 leading-snug mt-0.5">
                    {t.waiterCallReasonSubtitle}
                  </p>
                </div>
              </div>

              {/* 6-reason grid (3 × 2) */}
              <div className="grid grid-cols-3 gap-2.5 mt-5">
                {reasons.map((r) => {
                  const isSelected = selectedReason === r.code;
                  const Icon = r.icon;
                  return (
                    <button
                      key={r.code}
                      type="button"
                      onClick={() => setSelectedReason(r.code)}
                      className={`relative rounded-2xl p-3 transition-all active:scale-95 flex flex-col items-center justify-center gap-1.5 min-h-[92px] ${isSelected
                        ? `bg-gradient-to-br ${r.gradient} text-white shadow-lg ${r.shadow}`
                        : 'bg-gray-50 dark:bg-white/5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 border border-transparent hover:border-gray-200 dark:hover:border-white/10'
                        }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center ${isSelected
                          ? 'bg-white/25 backdrop-blur-sm'
                          : `bg-gradient-to-br ${r.iconBg}`
                          }`}
                      >
                        <Icon className={`w-5 h-5 ${isSelected ? 'text-white' : r.iconColor}`} />
                      </div>
                      <span
                        className={`text-[11px] font-bold leading-tight text-center ${isSelected ? 'text-white' : ''
                          }`}
                      >
                        {r.label}
                      </span>
                      {isSelected && (
                        <div className="absolute top-1.5 right-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5 text-white drop-shadow-md" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Optional note (collapsible toggle) */}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setNoteOpen((v) => !v)}
                  className="text-[12px] font-bold text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 inline-flex items-center gap-1 transition-colors"
                >
                  {noteOpen ? (
                    <ChevronUp className="w-3 h-3" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                  {t.waiterCallNoteLabel}
                </button>
                {noteOpen && (
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder={t.waiterCallNotePlaceholder}
                    maxLength={200}
                    rows={3}
                    className="mt-2 w-full bg-gray-50 dark:bg-white/5 border-2 border-transparent focus:border-orange-400 rounded-2xl p-3 text-[13px] outline-none font-medium resize-none transition-all placeholder:text-gray-400 dark:placeholder:text-gray-500 text-gray-900 dark:text-white"
                  />
                )}
              </div>

              {/* Submit button */}
              <button
                type="button"
                onClick={() => {
                  if (!selectedReason || !onSubmitWaiterCall) return;
                  onSubmitWaiterCall(selectedReason, noteText.trim() || null);
                }}
                disabled={!selectedReason || waiterCallLoading || !onSubmitWaiterCall}
                className={`w-full mt-5 flex items-center justify-center gap-2 px-5 py-4 rounded-2xl text-[15px] font-bold text-white transition-all ${!selectedReason || waiterCallLoading || !onSubmitWaiterCall
                  ? 'bg-gray-300 dark:bg-white/10 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-lg shadow-orange-500/30 active:scale-[0.98]'
                  }`}
              >
                {waiterCallLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t.waiterCallSubmitting}
                  </>
                ) : (
                  <>
                    <BellRing className="w-5 h-5" />
                    {t.waiterCallSubmit}
                  </>
                )}
              </button>
            </div>
          )}

          {/* ============================================================
                INLINE WAITER RATING — ofisant geldikten (acknowledged) ya da
                çağrı resolved olduktan sonra puanlama. localStorage flag ile
                bir kez verildi mi tekrar gösterilmez (kullanıcı isteği).
              ============================================================ */}
          {activeWaiterCall &&
            (activeWaiterCall.status === 'acknowledged' ||
              activeWaiterCall.status === 'resolved') &&
            activeWaiterCall.assigned_waiter &&
            lastOrderId &&
            !isWaiterRated && (
              <div className="bg-white dark:bg-[#1a1a1a] rounded-[28px] p-5 mb-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.2)] border border-gray-100 dark:border-white/5">
                <RatingPanel
                  title={
                    (t as any).rateWaiterTitle ||
                    `Ofisant ${activeWaiterCall.assigned_waiter.name}-a qiymət verin`
                  }
                  subtitle={
                    (t as any).rateWaiterSubtitle ||
                    'Xidmət təcrübənizi paylaşın.'
                  }
                  onSubmit={handleRateWaiter}
                  busy={waiterRatingBusy}
                  tone="indigo"
                  submitLabel={(t as any).submit || 'Göndər'}
                  commentPlaceholder={
                    (t as any).rateCommentPlaceholder ||
                    'Təçrübəniz haqqında yazın (opsional)'
                  }
                />
              </div>
            )}
          {activeWaiterCall &&
            activeWaiterCall.assigned_waiter &&
            isWaiterRated && (
              <div className="mb-4">
                <RatedBadge
                  title={
                    (t as any).waiterAlreadyRated ||
                    `Ofisanta verdiyiniz qiymət saxlandı`
                  }
                  rating={5}
                />
              </div>
            )}

          {/* ============================================================
                Quick action shortcuts (bill / special request)
              ============================================================ */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <button
              type="button"
              onClick={() => switchTab('history')}
              className="relative bg-gradient-to-br from-white to-gray-50 dark:from-[#1a1a1a] dark:to-[#1a1a1a] rounded-[24px] p-5 shadow-lg border-2 border-gray-100 dark:border-white/10 hover:scale-105 hover:shadow-xl active:scale-95 transition-all group overflow-hidden"
            >
              <div className="relative z-10 flex flex-col items-center text-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-500/20 dark:to-orange-500/10 flex items-center justify-center shadow-md">
                  <Receipt className="w-7 h-7 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <h4 className="text-sm font-black text-gray-900 dark:text-white mb-0.5">
                    {t.requestBill}
                  </h4>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                    {t.viewBill}
                  </p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setIsFeedbackOpen(true)}
              className="relative bg-gradient-to-br from-white to-gray-50 dark:from-[#1a1a1a] dark:to-[#1a1a1a] rounded-[24px] p-5 shadow-lg border-2 border-gray-100 dark:border-white/10 hover:scale-105 hover:shadow-xl active:scale-95 transition-all group overflow-hidden"
            >
              <div className="relative z-10 flex flex-col items-center text-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-500/20 dark:to-indigo-500/10 flex items-center justify-center shadow-md">
                  <MessageSquare className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h4 className="text-sm font-black text-gray-900 dark:text-white mb-0.5">
                    {(t as any).specialRequest || 'Xüsusi istək'}
                  </h4>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                    {(t as any).sendRequest || 'Talep göndər'}
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>
      )}

      {/* ============ HISTORY/PAYMENT TAB ============ */}
      {activeTab === 'history' && (
        <div className="px-5 py-8 pb-32 min-h-[60vh] animate-fade-in qrm-safe-top">
          {/* Title row */}
          <div className="flex items-center gap-2 mb-2">
            <button
              type="button"
              onClick={() => switchTab('home')}
              className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-white/5 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-900 dark:text-white" />
            </button>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t.liveBill}
            </h2>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 ml-11">
            {t.liveBillSubtitle}
          </p>

          {/* Live indicator + restaurant header */}
          <div className="ml-11 mb-5 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[11px] font-bold">
              {adisyonLoading ? (
                <RefreshCw className="w-3 h-3 animate-spin" />
              ) : (
                <Wifi className="w-3 h-3" />
              )}
              {adisyonLastUpdatedAt ? formatLiveAgo(adisyonLastUpdatedAt) : t.liveSyncedNow}
            </span>
            {adisyonError && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 text-[11px] font-bold">
                <WifiOff className="w-3 h-3" />
                {adisyonError}
              </span>
            )}
          </div>

          {/* ===== Bill card ===== */}
          <div className="bg-white dark:bg-[#1a1a1a] rounded-[24px] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.2)] border border-gray-100 dark:border-white/5 mb-6">
            {/* Bill header — restaurant + table */}
            <div className="bg-gradient-to-br from-orange-50 via-amber-50 to-white dark:from-orange-500/10 dark:via-amber-500/5 dark:to-[#1a1a1a] p-5 border-b border-gray-100 dark:border-white/5 flex justify-between items-center">
              <div className="min-w-0 flex-1">
                <span className="text-[11px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-wider">
                  {(() => {
                    const tn = safeText(billTable?.name) || safeText(masaName);
                    return tn
                      ? `${t.table_short} ${tn}`
                      : t.table_short;
                  })()}{' '}
                  • {t.today}
                </span>
                <h3 className="font-black text-gray-900 dark:text-white mt-1 text-lg truncate">
                  {safeTextOr(billRestaurant?.name, safeTextOr(restaurantName, t.bill))}
                </h3>
              </div>
              <div className="w-12 h-12 ml-3 shrink-0 rounded-2xl overflow-hidden bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400 flex items-center justify-center shadow-md">
                {(() => {
                  const logo = safeText(billRestaurant?.logo);
                  return logo ? (
                    <img
                      src={logo}
                      alt={safeTextOr(billRestaurant?.name, '')}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Receipt className="w-6 h-6" />
                  );
                })()}
              </div>
            </div>

            <div className="p-5">
              {/* Loading skeleton */}
              {adisyonLoading && !adisyon && (
                <div className="space-y-4">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="animate-pulse flex justify-between items-center">
                      <div className="flex gap-3 items-center">
                        <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-white/5" />
                        <div className="space-y-2">
                          <div className="h-3 w-32 bg-gray-100 dark:bg-white/5 rounded" />
                          <div className="h-2 w-20 bg-gray-100 dark:bg-white/5 rounded" />
                        </div>
                      </div>
                      <div className="h-3 w-16 bg-gray-100 dark:bg-white/5 rounded" />
                    </div>
                  ))}
                </div>
              )}

              {/* Empty state */}
              {!adisyonLoading && billOrders.length === 0 && (
                <div className="text-center py-10">
                  <Receipt className="w-14 h-14 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium">
                    {t.noOrdersOnTable}
                  </p>
                </div>
              )}

              {/* Per-order breakdown */}
              {billOrders.length > 0 && (
                <div className="space-y-6">
                  {billOrders.map((order) => (
                    <div
                      key={order.order_id}
                      className="border border-gray-100 dark:border-white/5 rounded-2xl overflow-hidden"
                    >
                      {/* Order header */}
                      <div className="bg-gray-50 dark:bg-white/5 px-4 py-2.5 flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <span className="text-[12px] font-black text-orange-600 dark:text-orange-400">
                            {safeTextOr(order.order_number, `#${order.order_id}`)}
                          </span>
                          <span className="text-[10px] text-gray-500 dark:text-gray-400">
                            {formatDate(safeText(order.created_at))}
                          </span>
                        </div>
                        <span className="text-[11px] font-bold text-gray-700 dark:text-gray-300">
                          {billCurrency} {formatPrice(safeNumber(order.totals?.net_total))}
                        </span>
                      </div>

                      {/* Stocks */}
                      <div className="p-4 space-y-3">
                        {coerceArray<typeof order.stocks[0]>(order.stocks as unknown).map((stock) => (
                          <div
                            key={`stock-${stock.pivot_id}`}
                            className="flex justify-between items-start gap-3"
                          >
                            <div className="flex gap-3 min-w-0 flex-1">
                              {safeText(stock.image) ? (
                                <img
                                  src={safeText(stock.image)}
                                  alt={safeTextOr(stock.name, '')}
                                  className="w-11 h-11 rounded-xl object-cover shrink-0 bg-gray-100 dark:bg-white/5"
                                />
                              ) : (
                                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-500/20 dark:to-amber-500/20 shrink-0 flex items-center justify-center">
                                  <Package className="w-5 h-5 text-orange-500" />
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <h4 className="font-bold text-gray-900 dark:text-white text-[14px] truncate">
                                  {safeTextOr(stock.name, '—')}
                                </h4>
                                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                                  <span className="text-[11px] text-gray-500 dark:text-gray-400 font-medium">
                                    {safeNumber(stock.quantity, 1)} × {billCurrency} {formatPrice(safeNumber(stock.unit_price))}
                                  </span>
                                  {stock.detail && (safeText(stock.detail.count) || safeText(stock.detail.unit)) && (
                                    <span className="text-[10px] text-gray-400 font-medium">
                                      • {safeText(stock.detail.count)} {safeText(stock.detail.unit)}
                                    </span>
                                  )}
                                  <span
                                    className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${kitchenStatusClasses(safeText(stock.kitchen_status))}`}
                                  >
                                    {kitchenStatusLabel(safeText(stock.kitchen_status))}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <span className="font-bold text-gray-900 dark:text-white text-[14px] whitespace-nowrap">
                              {billCurrency} {formatPrice(safeNumber(stock.total_price))}
                            </span>
                          </div>
                        ))}

                        {/* Stock sets */}
                        {coerceArray<typeof order.stock_sets[0]>(order.stock_sets as unknown).map((set) => (
                          <div
                            key={`set-${set.pivot_id}`}
                            className="flex justify-between items-start gap-3"
                          >
                            <div className="flex gap-3 min-w-0 flex-1">
                              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-100 to-fuchsia-100 dark:from-purple-500/20 dark:to-fuchsia-500/20 shrink-0 flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-purple-500" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <h4 className="font-bold text-gray-900 dark:text-white text-[14px] truncate">
                                  {safeTextOr(set.name, '—')}
                                </h4>
                                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                                  <span className="text-[11px] text-gray-500 dark:text-gray-400 font-medium">
                                    {safeNumber(set.quantity, 1)} × {billCurrency} {formatPrice(safeNumber(set.unit_price))}
                                  </span>
                                  <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 dark:bg-purple-500/10 dark:text-purple-400">
                                    {t.setMenu}
                                  </span>
                                  <span
                                    className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${kitchenStatusClasses(safeText(set.kitchen_status))}`}
                                  >
                                    {kitchenStatusLabel(safeText(set.kitchen_status))}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <span className="font-bold text-gray-900 dark:text-white text-[14px] whitespace-nowrap">
                              {billCurrency} {formatPrice(safeNumber(set.total_price))}
                            </span>
                          </div>
                        ))}

                        {/* Time charges */}
                        {coerceArray<typeof order.time_charges[0]>(order.time_charges as unknown).map((tc) => (
                          <div
                            key={`tc-${tc.id}`}
                            className="flex justify-between items-center gap-3 pt-2 border-t border-dashed border-gray-100 dark:border-white/5"
                          >
                            <div className="flex gap-2 items-center min-w-0 flex-1">
                              <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-500/10 text-blue-500 flex items-center justify-center shrink-0">
                                <Clock className="w-4 h-4" />
                              </div>
                              <span className="text-[13px] font-bold text-gray-700 dark:text-gray-300 truncate">
                                {safeTextOr(tc.title, t.timeChargeLabel)}
                              </span>
                            </div>
                            <span className="font-bold text-blue-600 dark:text-blue-400 text-[13px] whitespace-nowrap">
                              + {billCurrency} {formatPrice(safeNumber(tc.amount))}
                            </span>
                          </div>
                        ))}

                        {/* Discounts */}
                        {coerceArray<typeof order.discounts[0]>(order.discounts as unknown).map((d) => (
                          <div
                            key={`d-${d.id}`}
                            className="flex justify-between items-center gap-3"
                          >
                            <div className="flex gap-2 items-center min-w-0 flex-1">
                              <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0">
                                <Percent className="w-4 h-4" />
                              </div>
                              <span className="text-[13px] font-bold text-gray-700 dark:text-gray-300 truncate">
                                {safeTextOr(d.reason, t.manualDiscount)}
                              </span>
                            </div>
                            <span className="font-bold text-emerald-600 dark:text-emerald-400 text-[13px] whitespace-nowrap">
                              − {billCurrency} {formatPrice(safeNumber(d.amount))}
                            </span>
                          </div>
                        ))}

                        {/* Prepayments */}
                        {coerceArray<typeof order.prepayments[0]>(order.prepayments as unknown).map((p) => (
                          <div
                            key={`pp-${p.id}`}
                            className="flex justify-between items-center gap-3"
                          >
                            <div className="flex gap-2 items-center min-w-0 flex-1">
                              <div className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-indigo-500 flex items-center justify-center shrink-0">
                                <Tag className="w-4 h-4" />
                              </div>
                              <span className="text-[13px] font-bold text-gray-700 dark:text-gray-300 truncate">
                                {t.prepayment} • {paymentMethodLabel(safeText(p.type))}
                              </span>
                            </div>
                            <span className="font-bold text-indigo-600 dark:text-indigo-400 text-[13px] whitespace-nowrap">
                              − {billCurrency} {formatPrice(safeNumber(p.amount))}
                            </span>
                          </div>
                        ))}

                        {/* Partial payments */}
                        {coerceArray<typeof order.payments[0]>(order.payments as unknown).map((p) => (
                          <div
                            key={`pay-${p.id}`}
                            className="flex justify-between items-center gap-3"
                          >
                            <div className="flex gap-2 items-center min-w-0 flex-1">
                              <div className="w-8 h-8 rounded-lg bg-cyan-50 dark:bg-cyan-500/10 text-cyan-500 flex items-center justify-center shrink-0">
                                <Banknote className="w-4 h-4" />
                              </div>
                              <span className="text-[13px] font-bold text-gray-700 dark:text-gray-300 truncate">
                                {t.partialPayment} • {paymentMethodLabel(safeText(p.type))}
                              </span>
                            </div>
                            <span className="font-bold text-cyan-600 dark:text-cyan-400 text-[13px] whitespace-nowrap">
                              − {billCurrency} {formatPrice(safeNumber(p.amount))}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Grand totals */}
              {billTotals && billOrders.length > 0 && (() => {
                const itemsTotal = safeNumber(billTotals.items_total);
                const timeChargesTotal = safeNumber(billTotals.time_charges_total);
                const subtotalNum = safeNumber(billTotals.subtotal);
                const discountTotal = safeNumber(billTotals.discount_total);
                const netTotal = safeNumber(billTotals.net_total);
                const prepaymentTotal = safeNumber(billTotals.prepayment_total);
                const paymentTotal = safeNumber(billTotals.payment_total);
                const remaining = safeNumber(billTotals.remaining);
                return (
                  <div className="border-t border-dashed border-gray-200 dark:border-white/10 mt-6 pt-5 space-y-2">
                    <div className="flex justify-between text-[13px] text-gray-500 dark:text-gray-400 font-medium">
                      <span>{t.itemsTotal}</span>
                      <span>{billCurrency} {formatPrice(itemsTotal)}</span>
                    </div>
                    {timeChargesTotal > 0 && (
                      <div className="flex justify-between text-[13px] text-gray-500 dark:text-gray-400 font-medium">
                        <span>{t.timeChargesTotal}</span>
                        <span>+ {billCurrency} {formatPrice(timeChargesTotal)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-[13px] text-gray-700 dark:text-gray-300 font-bold pt-1 border-t border-gray-100 dark:border-white/5">
                      <span>{t.subtotal}</span>
                      <span>{billCurrency} {formatPrice(subtotalNum)}</span>
                    </div>
                    {discountTotal > 0 && (
                      <div className="flex justify-between text-[13px] text-emerald-600 dark:text-emerald-400 font-medium">
                        <span>{t.discountTotal}</span>
                        <span>− {billCurrency} {formatPrice(discountTotal)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-[13px] text-gray-700 dark:text-gray-300 font-bold">
                      <span>{t.netTotal}</span>
                      <span>{billCurrency} {formatPrice(netTotal)}</span>
                    </div>
                    {prepaymentTotal > 0 && (
                      <div className="flex justify-between text-[13px] text-indigo-600 dark:text-indigo-400 font-medium">
                        <span>{t.prepaymentTotal}</span>
                        <span>− {billCurrency} {formatPrice(prepaymentTotal)}</span>
                      </div>
                    )}
                    {paymentTotal > 0 && (
                      <div className="flex justify-between text-[13px] text-cyan-600 dark:text-cyan-400 font-medium">
                        <span>{t.paymentTotal}</span>
                        <span>− {billCurrency} {formatPrice(paymentTotal)}</span>
                      </div>
                    )}

                    {/* The hero — remaining */}
                    <div
                      className={`mt-3 p-4 rounded-2xl ${remaining > 0
                        ? 'bg-gradient-to-br from-orange-500 via-orange-600 to-amber-600 text-white shadow-lg shadow-orange-500/30'
                        : 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                        }`}
                    >
                      <div className="flex justify-between items-baseline">
                        <span className="text-[12px] font-bold uppercase tracking-wider opacity-90">
                          {remaining > 0 ? t.payable : t.fullyPaid}
                        </span>
                        <span className="text-[28px] font-black leading-none">
                          {billCurrency} {formatPrice(remaining)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Legacy fallback when adisyon hasn't loaded but we still have approved orders */}
              {!adisyonLoading && !adisyon && (approvedOrders.length > 0 || pendingOrders.length > 0) && (
                <div className="border-t border-dashed border-gray-200 dark:border-white/10 mt-2 pt-4 space-y-2">
                  <div className="space-y-3 mb-4">
                    {coerceArray<typeof approvedOrders[0]>(approvedOrders as unknown).map((order, oIdx) =>
                      coerceArray<{ name: unknown; quantity: unknown; price: unknown }>(
                        order.stocks as unknown,
                      ).map((stock, idx) => (
                        <div key={`la-${oIdx}-${idx}`} className="flex justify-between items-center">
                          <div className="flex gap-2 items-center">
                            <span className="font-bold text-gray-400">{safeNumber(stock.quantity, 1)}×</span>
                            <span className="font-bold text-gray-900 dark:text-white text-[14px]">
                              {safeTextOr(stock.name, '—')}
                            </span>
                          </div>
                          <span className="font-bold text-gray-900 dark:text-white text-[14px]">
                            {currency} {formatPrice(safeNumber(stock.price))}
                          </span>
                        </div>
                      )),
                    )}
                    {coerceArray<typeof pendingOrders[0]>(pendingOrders as unknown).map((order, oIdx) =>
                      coerceArray<{ name: unknown; quantity: unknown; price: unknown }>(
                        order.stocks as unknown,
                      ).map((stock, idx) => (
                        <div key={`lp-${oIdx}-${idx}`} className="flex justify-between items-center opacity-70">
                          <div className="flex gap-2 items-center">
                            <span className="font-bold text-amber-500">{safeNumber(stock.quantity, 1)}×</span>
                            <span className="font-bold text-gray-900 dark:text-white text-[14px]">
                              {safeTextOr(stock.name, '—')}
                            </span>
                          </div>
                          <span className="font-bold text-gray-900 dark:text-white text-[14px]">
                            {currency} {formatPrice(safeNumber(stock.price))}
                          </span>
                        </div>
                      )),
                    )}
                  </div>
                  <div className="flex justify-between text-[18px] font-black text-gray-900 dark:text-white pt-2 border-t border-gray-100 dark:border-white/5">
                    <span>{t.grandTotal}</span>
                    <span className="text-orange-500">
                      {currency} {formatPrice(totalAdisyon)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ===== Phone identify form ===== */}
          {!qrCustomer && onIdentifyCustomer && (
            <div className="bg-white dark:bg-[#1a1a1a] rounded-[24px] p-5 mb-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.2)] border border-gray-100 dark:border-white/5">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white flex items-center justify-center shadow-md shadow-orange-500/30">
                  <Phone className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-extrabold text-gray-900 dark:text-white text-[15px] truncate">
                    {t.enterPhone}
                  </h3>
                  <p className="text-[12px] text-gray-500 dark:text-gray-400 truncate">
                    {t.enterPhoneSubtitle}
                  </p>
                </div>
              </div>

              <form onSubmit={handlePhoneSubmit} className="space-y-3">
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={phoneInput}
                    onChange={(e) => setPhoneInput(e.target.value)}
                    placeholder="+994 50 123 45 67"
                    aria-label={t.phoneInputLabel}
                    className="w-full pl-11 pr-4 py-3.5 rounded-2xl text-[15px] outline-none font-semibold bg-gray-50 dark:bg-white/5 border-2 border-transparent focus:border-orange-400 focus:bg-white dark:focus:bg-white/10 transition-all placeholder:text-gray-400 dark:placeholder:text-gray-500 text-gray-900 dark:text-white"
                  />
                </div>

                {(identifyError || identifyNotFound) && (
                  <div className="flex items-start gap-2 text-[12px] text-red-500 dark:text-red-400 px-1">
                    <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>
                      {identifyNotFound ? t.phoneNotFound : identifyError || t.phoneInvalidFormat}
                    </span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={identifyLoading || phoneInput.trim().length < 4}
                  className={`w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl text-[15px] font-bold text-white shadow-lg active:scale-[0.98] transition-all ${identifyLoading || phoneInput.trim().length < 4
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-orange-500/30'
                    }`}
                >
                  {identifyLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      {t.identifying}
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      {t.continueAction}
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => setPhoneInput('')}
                  className="w-full text-center text-[13px] font-bold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  {t.continueAsGuest}
                </button>
              </form>
            </div>
          )}

          {/* ===== Loyalty / customer card ===== */}
          {qrCustomer && (
            <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 dark:from-amber-500/10 dark:via-orange-500/10 dark:to-rose-500/10 rounded-[24px] p-5 mb-6 border border-amber-100 dark:border-amber-500/20">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center shadow-md shadow-amber-500/40 shrink-0">
                    <Award className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
                      {t.customerHello(qrCustomer.name || t.guest)}
                    </p>
                    <p className="text-[12px] text-gray-500 dark:text-gray-400 truncate">
                      {qrCustomer.phone}
                    </p>
                  </div>
                </div>
                {onForgetCustomer && (
                  <button
                    type="button"
                    onClick={onForgetCustomer}
                    className="text-[11px] font-bold text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 inline-flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-white/50 dark:hover:bg-white/5 transition-colors"
                  >
                    <LogOut className="w-3 h-3" />
                    {t.forgetMe}
                  </button>
                )}
              </div>

              {/* Loyalty points block */}
              {qrCustomer.loyalty_points && (
                <div className="bg-white/70 dark:bg-black/20 backdrop-blur-sm rounded-2xl p-4 mb-3">
                  <div className="flex items-baseline justify-between mb-3">
                    <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t.loyaltyTitle}
                    </span>
                    <span className="text-[10px] font-bold text-gray-400">
                      ≈ {billCurrency} {formatPrice(qrCustomer.loyalty_points.value_in_currency)}
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-[36px] font-black text-amber-600 dark:text-amber-400 leading-none">
                      {qrCustomer.loyalty_points.balance.toLocaleString()}
                    </span>
                    <span className="text-[12px] font-bold text-gray-500 dark:text-gray-400">
                      {t.points}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-white/5">
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase">
                        {t.loyaltyTotalEarned}
                      </p>
                      <p className="text-[14px] font-black text-gray-900 dark:text-white">
                        {qrCustomer.loyalty_points.total_earned.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase">
                        {t.loyaltyTotalSpent}
                      </p>
                      <p className="text-[14px] font-black text-gray-900 dark:text-white">
                        {qrCustomer.loyalty_points.total_spent.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Customer balance */}
              {qrCustomer.balance > 0 && (
                <div className="bg-white/70 dark:bg-black/20 backdrop-blur-sm rounded-2xl p-4 mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-emerald-500" />
                    <span className="text-[12px] font-bold text-gray-700 dark:text-gray-300">
                      {t.customerBalance}
                    </span>
                  </div>
                  <span className="text-[16px] font-black text-emerald-600 dark:text-emerald-400">
                    {billCurrency} {formatPrice(qrCustomer.balance)}
                  </span>
                </div>
              )}

              {/* Lifetime stats */}
              {qrLifetime && qrLifetime.total_orders > 0 && (
                <div className="bg-white/70 dark:bg-black/20 backdrop-blur-sm rounded-2xl p-4">
                  <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    {t.lifetimeStats}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase">
                        {t.lifetimeOrders}
                      </p>
                      <p className="text-[14px] font-black text-gray-900 dark:text-white">
                        {qrLifetime.total_orders}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase">
                        {t.lifetimeSpent}
                      </p>
                      <p className="text-[14px] font-black text-gray-900 dark:text-white">
                        {billCurrency} {formatPrice(qrLifetime.total_spent)}
                      </p>
                    </div>
                    {qrLifetime.first_visit && (
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase">
                          {t.firstVisit}
                        </p>
                        <p className="text-[12px] font-bold text-gray-700 dark:text-gray-300">
                          {formatDate(qrLifetime.first_visit)}
                        </p>
                      </div>
                    )}
                    {qrLifetime.last_visit && (
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase">
                          {t.lastVisit}
                        </p>
                        <p className="text-[12px] font-bold text-gray-700 dark:text-gray-300">
                          {formatDate(qrLifetime.last_visit)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== Order history (paginated) ===== */}
          {qrCustomer && onLoadHistory && (
            <div className="bg-white dark:bg-[#1a1a1a] rounded-[24px] overflow-hidden mb-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.2)] border border-gray-100 dark:border-white/5">
              <button
                type="button"
                onClick={() => setHistoryExpanded((v) => !v)}
                className="w-full p-5 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 text-white flex items-center justify-center shadow-md shadow-indigo-500/30 shrink-0">
                    <History className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 text-left">
                    <h3 className="font-extrabold text-gray-900 dark:text-white text-[15px] truncate">
                      {t.historyTitle}
                    </h3>
                    <p className="text-[12px] text-gray-500 dark:text-gray-400 truncate">
                      {t.historySubtitle}
                    </p>
                  </div>
                </div>
                {historyExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
                )}
              </button>

              {historyExpanded && (
                <div className="px-5 pb-5 border-t border-gray-100 dark:border-white/5">
                  {historyError && (
                    <div className="text-[12px] text-red-500 dark:text-red-400 py-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {historyError}
                    </div>
                  )}

                  {historyLoading && !qrHistory && (
                    <div className="space-y-3 py-4">
                      {[0, 1, 2].map((i) => (
                        <div key={i} className="animate-pulse h-16 bg-gray-100 dark:bg-white/5 rounded-2xl" />
                      ))}
                    </div>
                  )}

                  {!historyLoading && qrHistory && coerceArray(qrHistory.orders as unknown).length === 0 && (
                    <div className="text-center py-8">
                      <Hourglass className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                      <p className="text-[13px] text-gray-500 dark:text-gray-400 font-medium">
                        {t.historyEmpty}
                      </p>
                    </div>
                  )}

                  {qrHistory && (() => {
                    const historyOrders = coerceArray<typeof qrHistory.orders[0]>(
                      qrHistory.orders as unknown,
                    );
                    if (historyOrders.length === 0) return null;
                    return (
                      <div className="space-y-3 pt-4">
                        {historyOrders.map((order) => {
                          // Defensive rendering — backend occasionally returns
                          // objects for items_summary / table_name when a locale
                          // column is being A/B-tested. safeText flattens them.
                          const orderNo = safeTextOr(order.order_number, `#${order.order_id}`);
                          const tableName = safeText(order.table_name);
                          const itemsSummary = safeText(order.items_summary);
                          const orderTotal = safeNumber(order.total);
                          const paidAmount = safeNumber(order.paid_amount);
                          const itemsCount = safeNumber(order.items_count);
                          const paymentMethods = coerceArray<unknown>(order.payment_methods as unknown);
                          return (
                            <div
                              key={order.order_id}
                              className="border border-gray-100 dark:border-white/5 rounded-2xl p-4 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                            >
                              <div className="flex justify-between items-start gap-3 mb-2">
                                <div className="flex gap-3 min-w-0 flex-1">
                                  {/* Receipt chip */}
                                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-500/15 dark:to-amber-500/10 text-orange-600 dark:text-orange-400 flex items-center justify-center shrink-0">
                                    <Receipt className="w-4 h-4" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                      <span className="text-[12px] font-black text-orange-600 dark:text-orange-400 truncate">
                                        {orderNo}
                                      </span>
                                      {tableName && (
                                        <>
                                          <span className="text-[10px] text-gray-400">•</span>
                                          <span className="text-[10px] text-gray-500 dark:text-gray-400 font-bold truncate">
                                            {tableName}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                    <p className="text-[11px] text-gray-400 dark:text-gray-500">
                                      {formatDate(order.date)}
                                    </p>
                                  </div>
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-[14px] font-black text-gray-900 dark:text-white whitespace-nowrap">
                                    {billCurrency} {formatPrice(orderTotal)}
                                  </p>
                                  <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold whitespace-nowrap">
                                    {t.paidLabel} {billCurrency} {formatPrice(paidAmount)}
                                  </p>
                                </div>
                              </div>

                              {itemsSummary && (
                                <p className="text-[12px] text-gray-600 dark:text-gray-300 mb-2 line-clamp-2 leading-snug">
                                  {itemsSummary}
                                </p>
                              )}

                              <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-dashed border-gray-100 dark:border-white/5">
                                <span className="text-[10px] text-gray-400 dark:text-gray-500 font-bold mr-1">
                                  {t.itemsCount(itemsCount)}
                                </span>
                                {paymentMethods.map((method, mi) => (
                                  <span
                                    key={`pm-${order.order_id}-${mi}`}
                                    className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-600 dark:bg-cyan-500/15 dark:text-cyan-300"
                                  >
                                    {paymentMethodLabel(safeText(method))}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })}

                        {/* Pagination */}
                        {(() => {
                          const lastPage = safeNumber(qrHistory.pagination?.last_page, 1);
                          const curPage = safeNumber(qrHistory.pagination?.page, 1);
                          const perPage = safeNumber(qrHistory.pagination?.per_page, 10);
                          if (lastPage <= 1) return null;
                          return (
                            <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-white/5">
                              <button
                                type="button"
                                disabled={curPage <= 1 || historyLoading}
                                onClick={() => onLoadHistory(curPage - 1, perPage)}
                                className={`flex items-center gap-1 px-3 py-2 rounded-xl text-[12px] font-bold transition-colors ${curPage <= 1 || historyLoading
                                  ? 'bg-gray-50 dark:bg-white/5 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                  : 'bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-white/10'
                                  }`}
                              >
                                <ChevronLeft className="w-3.5 h-3.5" />
                                {t.prevPage}
                              </button>
                              <span className="text-[12px] font-bold text-gray-500 dark:text-gray-400">
                                {t.pageOf(curPage, lastPage)}
                              </span>
                              <button
                                type="button"
                                disabled={curPage >= lastPage || historyLoading}
                                onClick={() => onLoadHistory(curPage + 1, perPage)}
                                className={`flex items-center gap-1 px-3 py-2 rounded-xl text-[12px] font-bold transition-colors ${curPage >= lastPage || historyLoading
                                  ? 'bg-gray-50 dark:bg-white/5 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                  : 'bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-white/10'
                                  }`}
                              >
                                {t.nextPage}
                                <ChevronRight className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          {/* ===== Payment Methods ===== */}
          {paymentEnabled && (billTotals?.remaining ?? 0) > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                {t.paymentMethod}
              </h3>

              {googlePayConfig ? (
                <div className="w-full">
                  <GooglePayButton
                    environment={googlePayConfig.environment || 'TEST'}
                    paymentRequest={googlePayConfig.paymentRequest}
                    onLoadPaymentData={onPayGooglePay}
                    buttonColor="black"
                    buttonType="pay"
                    buttonSizeMode="fill"
                    style={{ width: '100%', height: '56px' }}
                  />
                </div>
              ) : (
                <button
                  type="button"
                  disabled
                  className="w-full flex items-center justify-center gap-3 bg-white dark:bg-white/10 border-2 border-gray-200 dark:border-white/10 px-5 py-4 rounded-2xl text-[15px] font-bold text-gray-400 cursor-not-allowed shadow-sm"
                >
                  <svg className="h-5" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  <span>{t.googlePayNotConfigured}</span>
                </button>
              )}

              <button
                type="button"
                onClick={onPayApplePay}
                disabled={!applePayAvailable || isProcessingPayment}
                className="w-full flex items-center justify-center gap-3 bg-gray-900 dark:bg-white px-5 py-4 rounded-2xl text-[15px] font-bold text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 transition-all shadow-lg active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
                <span>{applePayAvailable ? t.payWithApple : t.applePayUnavailable}</span>
              </button>

              <button
                type="button"
                onClick={onPayOnline}
                disabled={isProcessingPayment}
                className={`w-full flex items-center justify-center gap-3 px-5 py-4 rounded-2xl text-[15px] font-bold text-white transition-all shadow-lg active:scale-[0.98] ${isProcessingPayment
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                  }`}
              >
                <CreditCard className="w-5 h-5" />
                <span>{isProcessingPayment ? t.processing : t.payWithCard}</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* ============ Bottom Navigation ============ */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 w-[calc(100%-40px)] max-w-sm bg-white/85 dark:bg-[#1a1a1a]/85 backdrop-blur-2xl border border-gray-200/50 dark:border-white/10 p-2 rounded-[32px] shadow-[0_20px_40px_rgb(0,0,0,0.1)] dark:shadow-[0_20px_40px_rgb(0,0,0,0.4)] flex items-center justify-between z-40 qrm-no-select">
        <button
          type="button"
          onClick={() => switchTab('home')}
          className={`flex flex-col items-center justify-center w-12 h-14 rounded-2xl transition-all ${activeTab === 'home'
            ? 'bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-400 hover:text-gray-900 dark:text-gray-500 dark:hover:text-white'
            }`}
          aria-label="home"
        >
          <HomeIcon className="w-6 h-6" />
        </button>
        <button
          type="button"
          onClick={() => switchTab('waiter')}
          className={`flex flex-col items-center justify-center w-12 h-14 rounded-2xl transition-all ${activeTab === 'waiter'
            ? 'bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-400 hover:text-gray-900 dark:text-gray-500 dark:hover:text-white'
            }`}
          aria-label="waiter"
        >
          <BellRing className="w-6 h-6" />
        </button>
        <button
          type="button"
          onClick={() => switchTab('tracking')}
          className={`flex flex-col items-center justify-center w-12 h-14 rounded-2xl transition-all ${activeTab === 'tracking'
            ? 'bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-400 hover:text-gray-900 dark:text-gray-500 dark:hover:text-white'
            }`}
          aria-label="tracking"
        >
          <Timer className="w-6 h-6" />
        </button>
        <button
          type="button"
          onClick={() => switchTab('history')}
          className={`flex flex-col items-center justify-center w-12 h-14 rounded-2xl transition-all ${activeTab === 'history'
            ? 'bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-400 hover:text-gray-900 dark:text-gray-500 dark:hover:text-white'
            }`}
          aria-label="history"
        >
          <FileText className="w-6 h-6" />
        </button>
        <button
          type="button"
          onClick={() => setIsCartOpen(true)}
          className="flex items-center justify-center gap-2 h-14 px-5 rounded-2xl bg-gradient-to-r from-gray-900 to-gray-800 dark:from-white dark:to-gray-200 text-white dark:text-gray-900 font-bold transition-all hover:scale-105 active:scale-95 shadow-lg shadow-gray-900/20 dark:shadow-white/20 ml-2"
          aria-label="cart"
        >
          <div className="relative">
            <ShoppingCart className="w-5 h-5" />
            {cartItemCount > 0 && (
              <span className="absolute -top-2 -right-3 bg-orange-500 text-white w-[18px] h-[18px] rounded-full flex items-center justify-center text-[10px] font-bold border-2 border-gray-900 dark:border-white shadow-sm">
                {cartItemCount}
              </span>
            )}
          </div>
          {cartTotal > 0 && (
            <span className="ml-1 tracking-tight whitespace-nowrap">
              {currency}
              {Math.round(cartTotal)}
            </span>
          )}
        </button>
      </div>

      {/* Cart */}
      <Cart
        items={cartItems}
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        onUpdateQuantity={onUpdateQuantity}
        onRemoveItem={onRemoveItem}
        onCheckout={onCheckout}
        currency={currency}
      />

      {/* Product Detail */}
      <ProductDetailModal
        product={selectedProduct}
        isOpen={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        onAddToCart={(p, q, d) => onAddToCart(p, q, d)}
        currency={currency}
        allProducts={products}
      />

      {/* Profile */}
      <ProfileModal
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
        masaName={masaName}
        theme={theme}
        onToggleTheme={onToggleTheme}
        user={user}
        onUserChange={onUserChange}
        googleClientId={googleClientId}
        appleServiceId={appleServiceId}
      />

      {/* Feedback / Special Request — şikayət/rəy için halen kullanılabilir,
          ama kullanıcı isterse rating modal'ı da kullanılabilir. */}
      <FeedbackModal
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
        showStarRating={!isRestaurantRated && !!restaurantId}
        onSubmit={(payload) => {
          if (onFeedback) onFeedback(payload);
          // FeedbackModal içinden gelen yıldız puanını backend'e gönder
          if (payload.starRating && restaurantId && !isRestaurantRated) {
            handleRateRestaurant(payload.starRating, payload.message || '');
          }
        }}
      />

      {/* Restaurant rating modal — 'Restorana qiymət ver' butonundan açılır. */}
      <RatingModal
        isOpen={isRestaurantRatingOpen}
        onClose={() => setIsRestaurantRatingOpen(false)}
        title={(t as any).rateRestaurantTitle || 'Restorana qiymət verin'}
        subtitle={
          (t as any).rateRestaurantSubtitle ||
          'Sizin rəyiniz bizə dəyərlidir.'
        }
        onSubmit={handleRateRestaurant}
        busy={restaurantRatingBusy}
        showCancel
        onCancel={() => setIsRestaurantRatingOpen(false)}
        tone="orange"
        submitLabel={(t as any).submit || 'Göndər'}
        cancelLabel={(t as any).cancel || 'Ləğv et'}
        commentPlaceholder={
          (t as any).rateCommentPlaceholder ||
          'Təçrübəniz haqqında yazın (opsional)'
        }
      />

      {/* Eski cart-notification özel bileşenini kaldırdık; bildirimler
          artık OrderDetailsQrcodV2 içindeki global <DynamicIslandProvider>
          altında island.show ile gösteriliyor. */}
    </div>
  );
}
